/**
 * Available Service Booking Functionality
 * Handles service product booking form logic similar to appointment form
 * Standalone implementation - no dependencies on appointment-form.js or product-form.js
 * 
 * Uses normal functions for better clarity and maintainability
 */

'use strict';

// API Base URL constant
const API_BASE_URL = '/apps/zg/';

// Simple in-memory caches (per page lifecycle)
// These are intentionally module-scoped so all drawers can share results.
const reasonsCache = {};           // key: speciesName.toLowerCase() -> reasons array
const reasonsRequestCache = {};    // key: speciesName.toLowerCase() -> Promise resolving to reasons array

const staffCache = {};             // key: `${clinicId}_${staffCategory}` -> full API response object
const staffRequestCache = {};      // key: `${clinicId}_${staffCategory}` -> Promise resolving to API response

// VetBuddy's lookup lags behind new records, so IDs registered on this page are kept here
// and a later booking reuses them instead of registering again.
const createdClientIds = {};       // key: 10-digit phone -> VetBuddy ClientID registered on this page
const createdPatientIds = {};      // key: `${clientId}|${pet name}|${species}` -> VetBuddy PatientID registered on this page

const timeSlotsCache = {};         // key: `${clinicId}_${staffId}_${date}` -> slots string
const timeSlotsRequestCache = {};  // key: `${clinicId}_${staffId}_${date}` -> Promise resolving to slots string

/**
 * Retry wrapper for API requests
 * Retries failed requests up to 3 times with exponential backoff
 * @param {Function} fetchFn - Function that returns a Promise (the fetch call)
 * @param {number} maxRetries - Maximum number of retry attempts (default: 3)
 * @param {number} currentAttempt - Current attempt number (for recursion)
 * @returns {Promise} - The fetch response or throws error after max retries
 */
async function retryFetch(fetchFn, maxRetries = 3, currentAttempt = 1) {
  try {
    const response = await fetchFn();
    
    // If response is not ok (status >= 400), check if we should retry
    if (!response.ok) {
      // Retry on 5xx server errors, but not on 4xx client errors
      if (response.status >= 500 && currentAttempt < maxRetries) {
        // Exponential backoff: wait 1s, 2s, 4s between retries
        const delay = Math.pow(2, currentAttempt - 1) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        return retryFetch(fetchFn, maxRetries, currentAttempt + 1);
      }
      // If it's a 4xx error or we've exhausted retries, return the response
      return response;
    }
    
    return response;
  } catch (error) {
    // Network errors or other exceptions - retry if we haven't exceeded max attempts
    if (currentAttempt < maxRetries) {
      // Exponential backoff: wait 1s, 2s, 4s between retries
      const delay = Math.pow(2, currentAttempt - 1) * 1000;
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryFetch(fetchFn, maxRetries, currentAttempt + 1);
    }
    // After max retries, throw the error
    throw error;
  }
}

// The whole journey (store page, card clicked, drawer edits) lives in one object under
// this key, so closing the drawer, refreshing or logging in never loses a choice.
const BOOKING_STORAGE_KEY = 'ziglyBooking';
// A saved date and slot older than this are dropped on open; the rest of the choices stay.
const BOOKING_SLOT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
// Used when the section does not set its own booking window.
const DEFAULT_BOOKING_WINDOW_DAYS = 30;
// Used when the section does not set how many services one order can hold.
const DEFAULT_MAX_SERVICES_PER_ORDER = 5;

// Field order of the drawer. A change in one field reloads only the fields below it.
// Date and time come before the doctor/groomer, so the customer sees every free time at the
// centre and then only the staff free at the time they chose.
const LEVEL = { city: 0, centre: 1, service: 2, packages: 3, slots: 4, doctors: 5 };

// Words every Zigly store and clinic name shares, so they say nothing about which store it is.
const NAME_STOP_WORDS = new Set([
  'zigly', 'pet', 'pets', 'care', 'store', 'clinic', 'hospital', 'vet', 'the', 'and', 'sec', 'sector',
  'new', 'pvt', 'ltd', 'products', 'grooming', 'multi', 'speciality', 'healthcare'
]);

/** "Zigly Pet Care - Kalyani Nagar | Pune" -> ['kalyani', 'nagar', 'pune'] */
function distinctiveNameWords(name) {
  return String(name || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word.length > 2 && !NAME_STOP_WORDS.has(word) && !/^\d+$/.test(word));
}

/** Number of distinctive words two store/clinic names share. */
function sharedNameWords(a, b) {
  const words = new Set(distinctiveNameWords(a));
  return distinctiveNameWords(b).filter((word) => words.has(word)).length;
}

/** VetBuddy files Dwarka under "New Delhi" and the rest of Delhi under "Delhi"; treat them as one city. */
function cityKey(cityName) {
  const name = String(cityName || '').trim();
  return (name.toLowerCase().startsWith('new ') ? name.substring(4) : name).trim().toLowerCase();
}

/** Dog / Cat from any species spelling ('Canine (Dog)', 'Feline', 'cats', '1'). */
function petTypeFromSpecies(species) {
  const value = String(species || '').trim().toLowerCase();
  if (value === '1' || /\b(canine|dog)s?\b/.test(value)) return 'Dog';
  if (value === '2' || /\b(feline|cat)s?\b/.test(value)) return 'Cat';
  return '';
}

/** "Everyday Shine Cat" and "Everyday Shine" are the same package for different pets. */
function packageFamily(title) {
  return String(title || '')
    .toLowerCase()
    .replace(/\b(cat|cats|dog|dogs|canine|feline|kitten|puppy)\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Local yyyy-mm-dd; toISOString() is UTC and gives yesterday before 05:30 IST. */
function toDateValue(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function readSavedBooking() {
  try {
    const raw = localStorage.getItem(BOOKING_STORAGE_KEY);
    const saved = raw ? JSON.parse(raw) : null;
    return saved && typeof saved === 'object' ? saved : null;
  } catch (error) {
    return null;
  }
}

/**
 * Initialize booking drawer for a specific product
 */
function initBookingDrawer(drawerElement, productData) {
    if (!drawerElement || !productData) return false;

    // Prevent duplicate event binding if this drawer has already been initialized.
    if (drawerElement.dataset.bookingDrawerBound === 'true') {
      return true;
    }

    const productId = drawerElement.dataset.productId || productData.productId;
    // Use unique-id if available (for duplicate product handling), otherwise fallback to productId
    const uniqueId = drawerElement.dataset.uniqueId || productId;

    // Form Elements - use uniqueId for all ID-based queries
    const form = drawerElement.querySelector('.available-service-drawer__form');
    const petSelect = drawerElement.querySelector(`#booking-pet-${uniqueId}`);
    const citySelect = drawerElement.querySelector(`#booking-city-${uniqueId}`);
    const clinicSelect = drawerElement.querySelector(`#booking-location-${uniqueId}`);
    const serviceTypeSelect = drawerElement.querySelector(`#booking-type-${uniqueId}`);
    const packageSelect = drawerElement.querySelector(`#booking-package-${uniqueId}`);
    const packageInfo = drawerElement.querySelector(`#package-info-${uniqueId}`);
    const doctorSelect = drawerElement.querySelector(`#booking-doctor-${uniqueId}`);
    const reasonForVisitInput = drawerElement.querySelector(`#booking-reason-${uniqueId}`);
    const dateInput = drawerElement.querySelector(`#booking-date-${uniqueId}`);
    const timeSlotSelect = drawerElement.querySelector(`#booking-time-slot-${uniqueId}`);
    const termsCheckbox = drawerElement.querySelector(`#booking-terms-${uniqueId}`);
    const submitButton = drawerElement.querySelector('.available-service-drawer__submit');
    const submitButtonText = drawerElement.querySelector('.available-service-drawer__submit-text');
    const submitButtonSpinner = drawerElement.querySelector('.loading__spinner');
    const timeSlotLoader = drawerElement.querySelector(`#timeSlotsLoader-${uniqueId}`);
    const dateWrapper = dateInput?.closest('.available-service-drawer__date-wrapper');

    const durationContainer = drawerElement.querySelector(`#duration-info-${uniqueId}`);
    const durationValue = durationContainer?.querySelector('[data-role="duration-value"]');

    // New Pet Fields
    const newPetDetails = drawerElement.querySelector(`#new-pet-details-${uniqueId}`);
    const newPetNameInput = drawerElement.querySelector(`#new-pet-name-${uniqueId}`);
    const newPetSpeciesSelect = drawerElement.querySelector(`#new-pet-species-${uniqueId}`);

    // Error Messages & Drawer Loading
    const errorContainer = drawerElement.querySelector(`#booking-errors-${uniqueId}`);
    const cartServiceMessage = drawerElement.querySelector(`#cart-service-message-${uniqueId}`);
    const guestPhoneNote = drawerElement.querySelector(`#guest-phone-note-${uniqueId}`);
    const drawerLoading = drawerElement.querySelector(`#drawer-loading-${uniqueId}`);

    // Required drawer controls. The top-level drawer can exist before all child
    // elements are rendered, so the function-level null guard is not enough.
    // Return early before binding any listeners to avoid repeated
    // "Cannot read properties of null (reading 'addEventListener')" errors.
    const requiredElements = {
      form,
      petSelect,
      citySelect,
      clinicSelect,
      serviceTypeSelect,
      packageSelect,
      doctorSelect,
      dateInput,
      timeSlotSelect,
      termsCheckbox,
      submitButton
    };

    const missingRequiredElements = Object.entries(requiredElements)
      .filter(([, element]) => !element)
      .map(([name]) => name);

    if (missingRequiredElements.length > 0) {
      // Log once per drawer so the console is not flooded during product-card
      // initialization. The observer can retry later when the missing markup is added.
      if (!drawerElement.dataset.bookingDrawerMissingLogged) {
        console.warn(
          `Booking drawer initialization skipped for ${drawerElement.id || uniqueId}. Missing: ${missingRequiredElements.join(', ')}`
        );
        drawerElement.dataset.bookingDrawerMissingLogged = 'true';
      }
      return false;
    }

    // Clear the one-time warning flag once the drawer has all required elements.
    delete drawerElement.dataset.bookingDrawerMissingLogged;

    // Read error messages from JSON script tag
    const errorMessagesScript = drawerElement.querySelector(`#error-messages-${uniqueId}`);
    let errorMessages = {
      serviceLimit: 'You can book up to [max] services in one order. Please check out the ones in your bag first.',
      generic: 'Something went wrong. Please try again.'
    };

    if (errorMessagesScript) {
      try {
        const parsed = JSON.parse(errorMessagesScript.textContent);
        if (parsed.errorMessageServiceLimit) {
          errorMessages.serviceLimit = parsed.errorMessageServiceLimit;
        }
        if (parsed.errorMessageGeneric) {
          errorMessages.generic = parsed.errorMessageGeneric;
        }
      } catch (e) {
        console.error('Error parsing error messages:', e);
      }
    }

    const bookingWindowDays = parseInt(form.dataset.bookingWindowDays || '', 10) > 0
      ? parseInt(form.dataset.bookingWindowDays, 10)
      : DEFAULT_BOOKING_WINDOW_DAYS;
    const maxServicesPerOrder = parseInt(form.dataset.maxServices || '', 10) > 0
      ? parseInt(form.dataset.maxServices, 10)
      : DEFAULT_MAX_SERVICES_PER_ORDER;

    // State Variables (scoped to this drawer instance)
    let clientData = null;
    let isUsingTemporaryPhone = true;
    let isNewPet = false;
    let cartServiceLines = null;       // Service bookings already in the bag; null = not read yet
    let isSubmitting = false;
    let runSeq = 0;                    // Latest cascade run; older runs drop their results
    let lastStaffSlots = null;         // [{ staff, slots }] behind the times shown, so they can be re-rendered without a refetch
    let slotStaff = new Map();         // time range -> { startTime, staff: [free for the whole range] }
    let slotsLoading = false;          // more doctors/groomers' times are still on their way

    let catalog = [];                  // Every package card on the page
    let services = [];                 // Services on the page, in toggle order
    let cityGroups = new Map();        // cityKey -> { key, label, clinics }
    let clinicsUnavailable = false;    // VetBuddy's clinic list could not be loaded
    let stores = [];                   // Store metaobjects embedded in the page, for service tags
    let packagesForPet = [];           // Packages shown in the dropdown right now
    let selectedPackage = null;        // One of packagesForPet
    let matchedReasonData = null;      // VetBuddy reason for the selected package

    // The booking. Saved to localStorage on every change.
    let booking = emptyBooking();

    function emptyBooking() {
      return {
        petId: '',
        petName: '',
        petType: '',
        city: '',
        centreId: '',
        service: '',
        packageId: '',
        reasonForVisitId: '',
        doctorId: '',
        date: '',
        slot: '',
        source: 'journey',
        pageStoreId: '',
        cardPackageId: '',
        savedAt: ''
      };
    }

    function saveBooking() {
      booking.savedAt = new Date().toISOString();
      try {
        localStorage.setItem(BOOKING_STORAGE_KEY, JSON.stringify(booking));
      } catch (error) {
        // Private mode or full storage: the drawer still works, it just won't remember.
      }
    }

    // ------------------------------------------------------------------------
    // Field status lines (loader, error + Retry, "no longer available")
    // ------------------------------------------------------------------------

    const fieldControls = {
      pet: petSelect,
      city: citySelect,
      centre: clinicSelect,
      service: serviceTypeSelect,
      package: packageSelect,
      doctor: doctorSelect,
      date: dateInput,
      slot: timeSlotSelect
    };

    function statusLine(field) {
      const control = fieldControls[field];
      if (!control) return null;
      const group = control.closest('.available-service-drawer__form-group');
      if (!group) return null;
      let line = group.querySelector(`[data-field-status="${field}"]`);
      if (!line) {
        line = document.createElement('p');
        line.className = 'available-service-drawer__field-status hidden';
        line.dataset.fieldStatus = field;
        line.setAttribute('role', 'status');
        line.setAttribute('aria-live', 'polite');
        const anchor = control.closest('.available-service-drawer__custom-select, .available-service-drawer__date-wrapper') || control;
        anchor.insertAdjacentElement('afterend', line);
      }
      return line;
    }

    /**
     * kind: 'loading' | 'error' | 'info' | null (hide).
     * An error with retryLevel gets a Retry link that re-runs the cascade from that field.
     */
    function setStatus(field, kind, message, retryLevel) {
      const line = statusLine(field);
      if (!line) return;
      line.classList.remove('is-loading', 'is-error', 'is-info');
      if (!kind) {
        line.textContent = '';
        line.classList.add('hidden');
        return;
      }
      line.classList.remove('hidden');
      line.classList.add(`is-${kind}`);
      line.textContent = message || (kind === 'loading' ? 'Loading...' : '');
      if (retryLevel !== undefined) {
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'available-service-drawer__field-retry';
        retry.textContent = 'Retry';
        retry.addEventListener('click', () => {
          setStatus(field, null);
          if (retryLevel === 'clients') {
            loadClientsAndCities().then(() => refreshFrom(LEVEL.city));
          } else {
            refreshFrom(retryLevel);
          }
        });
        line.append(' ', retry);
      }
    }

    function resetSelect(select, placeholder) {
      select.innerHTML = '';
      const option = document.createElement('option');
      option.value = '';
      option.textContent = placeholder;
      select.appendChild(option);
      select.value = '';
      select.disabled = true;
    }

    /** Fill a select; returns true when `keepValue` is still one of the options. */
    function fillSelect(select, placeholder, items, keepValue) {
      resetSelect(select, placeholder);
      items.forEach((item) => {
        const option = document.createElement('option');
        option.value = item.value;
        option.textContent = item.label;
        if (item.data) {
          Object.keys(item.data).forEach((key) => {
            option.dataset[key] = item.data[key];
          });
        }
        select.appendChild(option);
      });
      select.disabled = items.length === 0;
      const kept = !!keepValue && items.some((item) => String(item.value) === String(keepValue));
      select.value = kept ? String(keepValue) : '';
      return kept;
    }

    // ------------------------------------------------------------------------
    // Page context: package cards, services and stores on this page
    // ------------------------------------------------------------------------

    function staffCategoryFor(serviceName) {
      return /groom/i.test(serviceName || '') ? 'groomer' : 'doctor';
    }

    function readCatalog() {
      const seen = new Set();
      const items = [];
      document.querySelectorAll('[data-role="product-slide"]').forEach((slide) => {
        const card = slide.querySelector('.available-service-card');
        if (!card || !card.dataset.variantId) return;
        const service = (slide.dataset.serviceName || '').trim();
        const petType = petTypeFromSpecies(slide.dataset.species);
        const key = `${card.dataset.variantId}|${service}|${petType}`;
        if (!service || !petType || seen.has(key)) return;
        seen.add(key);

        const price = card.querySelector('.available-service-card__price-discounted-price');
        const advance = card.querySelector('.available-service-card__booking-advance');
        items.push({
          variantId: String(card.dataset.variantId),
          productId: card.dataset.productId || '',
          title: (card.dataset.productTitle || '').trim(),
          service,
          petType,
          reasonKey: (card.dataset.reasonForVisit || '').trim(),
          duration: card.dataset.serviceDuration || '',
          priceText: price ? price.textContent.trim() : '',
          advanceText: advance ? advance.textContent.trim() : ''
        });
      });
      return items;
    }

    function readStores() {
      const dataScript = document.getElementById('store-detail-data') || document.getElementById('store-locator-data');
      if (!dataScript) return [];
      try {
        const data = JSON.parse(dataScript.textContent);
        return Array.isArray(data) ? data : [];
      } catch (error) {
        return [];
      }
    }

    function readSelectedStore() {
      try {
        const raw = localStorage.getItem('selectedStore');
        return raw ? JSON.parse(raw) : null;
      } catch (error) {
        return null;
      }
    }

    /**
     * The Shopify store record for a VetBuddy clinic. Store IDs in Shopify are not unique
     * (40 is both BTM Layout and M3M Atrium), so an ID only counts when the city matches too;
     * otherwise fall back to the store whose name shares the most words with the clinic's.
     */
    function storeForClinic(clinic) {
      if (!clinic) return null;
      const sameCity = stores.filter((store) => cityKey(store.city) === cityKey(clinic.ClinicCity));
      const byId = sameCity.find((store) => String(store.storeId || '') === String(clinic.ClinicId));
      if (byId) return byId;
      let best = null;
      let bestScore = 0;
      sameCity.forEach((store) => {
        const score = sharedNameWords(store.storeDetailPageTitle || store.name, clinic.ClinicName);
        if (score > bestScore) {
          best = store;
          bestScore = score;
        }
      });
      return best;
    }

    /** true / false when the store's service tags say, null when we don't know. */
    function clinicOffersService(clinic, serviceName) {
      const store = storeForClinic(clinic);
      const tags = store && store.serviceTags;
      const list = Array.isArray(tags) ? tags : String(tags || '').split(',');
      const clean = list.map((tag) => String(tag).trim().toLowerCase()).filter(Boolean);
      if (!clean.length) return null;
      if (staffCategoryFor(serviceName) === 'groomer') return clean.some((tag) => tag.includes('groom'));
      return clean.some((tag) => tag.includes('vet'));
    }

    // ------------------------------------------------------------------------
    // VetBuddy reads
    // ------------------------------------------------------------------------

    /**
     * Extract phone number from customer.phone (remove country code)
     */
    function extractPhoneNumber(phoneString) {
      if (!phoneString) return null;
      // Remove all non-numeric characters
      const cleaned = phoneString.replace(/[^0-9]/g, '');
      // Get last 10 digits
      return cleaned.slice(-10);
    }

    function customerPhoneNumber() {
      let customerPhone = drawerElement.dataset.customerPhone;
      if (!customerPhone) {
        const customerPhoneScript = document.querySelector(`script#customer-phone-data-${uniqueId}`);
        if (customerPhoneScript) {
          try {
            customerPhone = JSON.parse(customerPhoneScript.textContent).phone || '';
          } catch (e) {
            customerPhone = customerPhoneScript.getAttribute('data-customer-phone') || '';
          }
          if (customerPhone) drawerElement.dataset.customerPhone = customerPhone;
        }
      }
      const phone = extractPhoneNumber(customerPhone);
      return phone && phone.length === 10 ? phone : null;
    }

    /**
     * Client (with pets) and clinics for the customer. Guests and first-time customers
     * get the clinic list only. Null when the clinic list itself could not be loaded.
     */
    async function fetchClientData(phoneNumber) {
      try {
        if (phoneNumber) {
          // Goes through the VetBuddy middleware (assets/vetbuddy-api.js). VetBuddy
          // tokens cannot be created from a browser, so the middleware creates the
          // token server-side and returns the client, pets and clinics to us.
          const data = await window.ZiglyVetBuddy.getClient(phoneNumber);
          if (data && data.responseCode === 0 && data.status === 'success' && data.data) {
            const { client, patients, clinics } = data.data;
            if (clinics && clinics.length) {
              return { ...client, patients: patients || [], clinics };
            }
          }
        }

        // No VetBuddy client for this number yet (first-time customer) or a guest. They still
        // need the clinic list to pick a clinic and book, so fetch the clinics on their own.
        const clinicsOnly = await window.ZiglyVetBuddy.getClinics();
        const clinics = (clinicsOnly && clinicsOnly.data && clinicsOnly.data.clinics) || [];
        return clinics.length ? { patients: [], clinics } : null;
      } catch (error) {
        console.warn('[Zigly booking] Client/clinic lookup failed:', error.message);
        return null;
      }
    }

    /**
     * Normalize customer-facing species names to the API names used by reasons.
     */
    function normalizeSpeciesForApi(speciesName) {
      if (!speciesName) return '';
      const normalized = String(speciesName).trim().toLowerCase();

      // VetBuddy names a pet's species 'Canine (Dog)' but plain 'Feline', and reasons are
      // filed under 'Canine (Dog)' and 'Feline (Cat)'. Match the species word wherever it
      // sits rather than the whole string, so the suffix does not lose us the match.
      if (/\b(canine|dog)s?\b/.test(normalized)) {
        return 'Canine';
      }

      if (/\b(feline|cat)s?\b/.test(normalized)) {
        return 'Feline';
      }

      return String(speciesName).trim();
    }

    /**
     * Fetch reasons for visit from API based on species
     */
    async function fetchReasonsForVisit(speciesName) {
      if (!speciesName) return null;

      const apiSpeciesName = normalizeSpeciesForApi(speciesName);
      if (!apiSpeciesName) return null;

      const cacheKey = apiSpeciesName.toLowerCase();

      // Use cached reasons if available
      if (reasonsCache[cacheKey]) {
        return reasonsCache[cacheKey];
      }

      // If a request for this species is already in-flight, reuse it
      if (reasonsRequestCache[cacheKey]) {
        return reasonsRequestCache[cacheKey];
      }

      // Create and store the in-flight request promise
      const requestPromise = (async () => {
        try {
          const data = await window.ZiglyVetBuddy.getReasons(apiSpeciesName);

          if (data.responseCode === 0 && data.status === 'success' && data.data) {
            reasonsCache[cacheKey] = data.data;
            return data.data;
          }
          return null;
        } catch (error) {
          return null;
        } finally {
          // Clear in-flight marker regardless of success/failure
          delete reasonsRequestCache[cacheKey];
        }
      })();

      reasonsRequestCache[cacheKey] = requestPromise;
      return requestPromise;
    }

    /**
     * Parse metafield value to extract main reason part (ignore time)
     * Examples:
     *   "20-40 pounds Approx. 15min" -> "20-40 pounds"
     *   "Basic Grooming Approx. 60min" -> "Basic Grooming"
     *   "Annual Health check up" -> "Annual Health check up"
     */
    function parseMetafieldValue(metafieldValue) {
      if (!metafieldValue) return '';

      // Remove time-related text (Approx., approx, min, mins, minutes, etc.)
      // Also handle patterns like "Approx. 15min", "approx 15 min", etc.
      let parsed = metafieldValue
        .replace(/\s*approx\.?\s*\d+\s*(min|mins|minutes?)?/gi, '')
        .replace(/\s*\d+\s*(min|mins|minutes?)\s*/gi, '')
        .trim();

      return parsed;
    }

    /**
     * Normalize string for comparison (lowercase, trim, remove extra spaces, special chars)
     */
    function normalizeString(str) {
      if (!str) return '';
      return str
        .toLowerCase()
        .trim()
        .replace(/\s+/g, ' ')
        .replace(/[^\w\s-]/g, '') // Remove special characters except hyphens
        .replace(/\s*-\s*/g, '-'); // Normalize hyphens
    }

    /**
     * Check if string contains weight range pattern (e.g., "20-40", "over 50", "under 20")
     */
    function isWeightRange(str) {
      const weightPatterns = [
        /\d+\s*-\s*\d+/,           // "20-40", "20 - 40"
        /over\s+\d+/,              // "over 50", "Over 50"
        /under\s+\d+/,             // "under 20", "Under 20"
        /\d+\s*to\s*\d+/           // "20 to 40"
      ];
      return weightPatterns.some(pattern => pattern.test(str.toLowerCase()));
    }

    /**
     * Extract weight range from string (e.g., "20-40 pounds" -> {min: 20, max: 40})
     * Returns null if not a weight range
     */
    function extractWeightRange(str) {
      const normalized = str.toLowerCase().trim();

      // Match "over X" or "over X pounds"
      const overMatch = normalized.match(/over\s+(\d+)/);
      if (overMatch) {
        return { min: parseInt(overMatch[1]), max: Infinity, type: 'over' };
      }

      // Match "under X" or "under X pounds"
      const underMatch = normalized.match(/under\s+(\d+)/);
      if (underMatch) {
        return { min: 0, max: parseInt(underMatch[1]), type: 'under' };
      }

      // Match "X-Y" or "X to Y"
      const rangeMatch = normalized.match(/(\d+)\s*[-to]\s*(\d+)/);
      if (rangeMatch) {
        return {
          min: parseInt(rangeMatch[1]),
          max: parseInt(rangeMatch[2]),
          type: 'range'
        };
      }

      return null;
    }

    /**
     * Check if two weight ranges overlap or match
     */
    function weightRangesMatch(range1, range2) {
      if (!range1 || !range2) return false;

      // Exact match
      if (range1.type === range2.type &&
          range1.min === range2.min &&
          range1.max === range2.max) {
        return true;
      }

      // Check overlap for ranges
      if (range1.type === 'range' && range2.type === 'range') {
        // Ranges overlap if: range1.min <= range2.max && range1.max >= range2.min
        return range1.min <= range2.max && range1.max >= range2.min;
      }

      // "Over X" matches if other range's max >= X
      if (range1.type === 'over' && range2.type === 'range') {
        return range2.max >= range1.min;
      }
      if (range2.type === 'over' && range1.type === 'range') {
        return range1.max >= range2.min;
      }

      // "Under X" matches if other range's min < X
      if (range1.type === 'under' && range2.type === 'range') {
        return range2.min < range1.max;
      }
      if (range2.type === 'under' && range1.type === 'range') {
        return range1.min < range2.max;
      }

      return false;
    }

    /**
     * Flexible text match (checks if strings are similar after normalization)
     * Handles case-insensitive matching and minor variations
     */
    function textMatch(str1, str2) {
      const normalized1 = normalizeString(str1);
      const normalized2 = normalizeString(str2);

      // Exact match after normalization
      if (normalized1 === normalized2) {
        return true;
      }

      // Check if one contains the other (for partial matches)
      if (normalized1.includes(normalized2) || normalized2.includes(normalized1)) {
        // Only consider it a match if the difference is reasonable
        const lengthDiff = Math.abs(normalized1.length - normalized2.length);
        const shorterLength = Math.min(normalized1.length, normalized2.length);
        // If difference is less than 30% of shorter string, consider it a match
        if (lengthDiff / shorterLength < 0.3) {
          return true;
        }
      }

      return false;
    }

    /**
     * Match a package's reason_for_visit metafield with the VetBuddy reasons.
     *
     * Supports both:
     *  - ID-based values (metafield stores ReasonForVisitID as a simple value, e.g. "1234")
     *  - Text-based values (metafield stores the human-readable reason)
     *
     * Returns matched reason data if found, null otherwise.
     */
    function matchReasonWithMetafield(metafieldValue, apiReasons) {
      if (!metafieldValue || !apiReasons || apiReasons.length === 0) {
        return null;
      }

      const rawValue = String(metafieldValue).trim();
      if (!rawValue) {
        return null;
      }

      // 1) An all-digit metafield is a ReasonForVisitID, and only an exact ID match counts:
      // matching "650" by text would pick an unrelated reason.
      if (/^[0-9]+$/.test(rawValue)) {
        return apiReasons.find((reason) => reason && String(reason.ReasonForVisitID).trim() === rawValue) || null;
      }

      // 2) Text / weight-range based matching

      // Parse metafield to extract main reason part (ignore time)
      const parsedMetafield = parseMetafieldValue(rawValue);
      if (!parsedMetafield) {
        return null;
      }

      // Check if metafield contains a weight range
      const isMetafieldWeightRange = isWeightRange(parsedMetafield);
      const metafieldWeightRange = isMetafieldWeightRange ? extractWeightRange(parsedMetafield) : null;

      // Try to find a match
      const matchedReason = apiReasons.find(reason => {
        const reasonName = reason.ReasonForVisitName || '';
        if (!reasonName) return false;

        // If metafield is a weight range, try weight range matching
        if (isMetafieldWeightRange && isWeightRange(reasonName)) {
          const reasonWeightRange = extractWeightRange(reasonName);
          if (reasonWeightRange && weightRangesMatch(metafieldWeightRange, reasonWeightRange)) {
            return true;
          }
        }

        // Try flexible text matching (for both weight ranges and text reasons)
        if (textMatch(parsedMetafield, reasonName)) {
          return true;
        }

        return false;
      });

      return matchedReason || null;
    }

    /**
     * Doctors or groomers at a clinic. Resolves to { ok, staff } - ok false means the
     * lookup itself failed (show Retry), an empty list means nobody is available.
     */
    async function fetchStaff(clinicId, staffCategory) {
      const cacheKey = `${clinicId}_${staffCategory}`;
      if (staffCache[cacheKey]) return { ok: true, staff: staffCache[cacheKey].data };

      if (!staffRequestCache[cacheKey]) {
        staffRequestCache[cacheKey] = window.ZiglyVetBuddy.getStaff(clinicId, staffCategory)
          .finally(() => {
            delete staffRequestCache[cacheKey];
          });
      }

      try {
        const data = await staffRequestCache[cacheKey];
        if (data && data.responseCode === 0 && data.status === 'success' && Array.isArray(data.data)) {
          staffCache[cacheKey] = data;
          return { ok: true, staff: data.data };
        }
        // The client turns VetBuddy's "no data found" into an empty error response.
        if (data && Array.isArray(data.data)) return { ok: true, staff: [] };
        console.warn('[Zigly booking] Staff lookup failed', { clinicId, staffCategory, response: data });
        return { ok: false, staff: [] };
      } catch (error) {
        console.warn('[Zigly booking] Staff lookup failed', { clinicId, staffCategory, error: error.message });
        return { ok: false, staff: [] };
      }
    }

    /**
     * VetBuddy's slot string for a staff member on a date ('' = no rota that day).
     * fresh skips the cache, for the rechecks before the booking goes to the bag.
     * Throws when the lookup fails.
     */
    async function fetchSlotsString(clinicId, staffId, date, fresh) {
      const cacheKey = `${clinicId}_${staffId}_${date}`;
      if (!fresh && timeSlotsCache[cacheKey] !== undefined) return timeSlotsCache[cacheKey];

      if (fresh || !timeSlotsRequestCache[cacheKey]) {
        const dateParts = date.split('-');
        const formattedDate = `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;
        const request = window.ZiglyVetBuddy.getStaffSlots(clinicId, staffId, formattedDate)
          .then((data) => {
            if (!data || data.responseCode !== 0 || data.status !== 'success') {
              console.warn('[Zigly booking] Slot lookup failed', { clinicId, staffId, date, response: data });
              throw new Error('Slot lookup failed');
            }
            const rota = (data.data || []).find((item) => String(item.StaffID) === String(staffId)) || (data.data || [])[0];
            const slots = (rota && rota.Slots) || '';
            timeSlotsCache[cacheKey] = slots;
            return slots;
          })
          .finally(() => {
            if (timeSlotsRequestCache[cacheKey] === request) delete timeSlotsRequestCache[cacheKey];
          });
        timeSlotsRequestCache[cacheKey] = request;
      }

      return timeSlotsRequestCache[cacheKey];
    }

    // ------------------------------------------------------------------------
    // Slot maths
    // ------------------------------------------------------------------------

    /**
     * Minutes this service takes, or null when nothing reliable says.
     * VetBuddy's TimeTakenMins wins; the product metafield is only a stand-in for
     * services VetBuddy has no duration for. Never guess a number - a wrong duration
     * either double-books the doctor (too short) or hides free slots (too long).
     */
    function resolveServiceDurationMins() {
      const fromApi = matchedReasonData && parseInt(matchedReasonData.TimeTakenMins, 10);
      if (Number.isFinite(fromApi) && fromApi > 0) return fromApi;

      const fromMetafield = parseInt((selectedPackage && selectedPackage.duration) || '', 10);
      if (Number.isFinite(fromMetafield) && fromMetafield > 0) return fromMetafield;

      return null;
    }

    /** Show the "takes about N minutes" box, or hide it when no duration is known. */
    function updateDurationBox() {
      if (!durationContainer || !durationValue) return;

      const duration = selectedPackage ? resolveServiceDurationMins() : null;
      if (!duration) {
        durationValue.textContent = '';
        durationContainer.classList.add('hidden');
        durationContainer.style.opacity = '0';
        durationContainer.style.transform = 'translateY(-10px)';
        return;
      }

      durationValue.textContent = duration;
      durationContainer.classList.remove('hidden');
      // Force a reflow for animation
      durationContainer.offsetHeight;
      durationContainer.style.opacity = '1';
      durationContainer.style.transform = 'translateY(0)';
    }

    /**
     * Start times the selected package fits in on this date. Times already gone are
     * left out when the date is today.
     */
    function bookableSlots(slotsString, date, staffId, keepBagHeld) {
      // VetBuddy always returns a fixed grid of equal blocks ("09:00-09:10 OK, ...").
      // It knows nothing about the chosen service, so the service duration is applied here.
      const blocks = parseSlotBlocks(slotsString || '');
      const duration = resolveServiceDurationMins();
      let starts = duration ? bookableStarts(blocks, duration) : blocks.filter((block) => block.isFree);

      if (date === toDateValue(new Date())) {
        const now = new Date();
        const nowMins = now.getHours() * 60 + now.getMinutes();
        starts = starts.filter((block) => (toMinutes(block.startTime) || 0) > nowMins);
      }
      // Times held by a booking already in the bag are not free, though VetBuddy says so.
      return keepBagHeld ? starts : starts.filter((block) => !clashingCartBooking(block.startTime, staffId));
    }

    /**
     * Parse VetBuddy's slot string into blocks on a single rising timeline.
     * A 24 hour rota ends "23:50-00:00", so times are carried past midnight rather
     * than wrapping back to 0 - otherwise a block would look like it ran backwards
     * and appointments across midnight would be dropped.
     */
    function parseSlotBlocks(slotsString) {
      const blocks = [];
      let dayOffset = 0;
      let previousStart = -1;

      String(slotsString).split(',').forEach((slot) => {
        const parts = slot.trim().split(' ');
        if (parts.length < 2) return;

        const range = parts[0];
        const [startTime, endTime] = range.split('-');
        const startRaw = toMinutes(startTime);
        const endRaw = toMinutes(endTime);
        if (startRaw === null || endRaw === null) return;

        // The clock going backwards means the rota has crossed into the next day.
        if (startRaw < previousStart) dayOffset += 1440;
        previousStart = startRaw;

        const startMins = startRaw + dayOffset;
        // "23:50-00:00" ends on the next day, so carry the end past midnight too.
        const endMins = endRaw > startRaw ? endRaw + dayOffset : endRaw + dayOffset + 1440;

        // "OK|1" is inside the rota but already holds 1 appointment (it is the booked
        // block on the VetBuddy calendar), so only a bare "OK" can still be booked.
        const [state, bookedCount] = parts[1].split('|');
        blocks.push({
          range,
          startTime,
          startMins,
          endMins,
          isFree: state === 'OK' && !(parseInt(bookedCount, 10) > 0),
        });
      });

      return blocks;
    }

    /** "09:30" -> 570. Null when unparseable. */
    function toMinutes(timeStr) {
      const parts = String(timeStr).trim().split(':');
      if (parts.length !== 2) return null;
      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10);
      if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
      return hours * 60 + minutes;
    }

    /**
     * Start times a service of `duration` minutes actually fits in.
     * A 30 minute service needs its whole 30 minutes free, so every block it runs
     * through must be free and follow on from the last with no gap - offering a start
     * whose later blocks are taken would double-book the doctor.
     */
    function bookableStarts(blocks, duration) {
      const starts = [];

      for (let i = 0; i < blocks.length; i++) {
        if (!blocks[i].isFree) continue;

        const needUntil = blocks[i].startMins + duration;
        let covered = blocks[i].endMins;
        let j = i;

        while (covered < needUntil) {
          const next = blocks[j + 1];
          // Stop at a taken block or a break in the day (lunch, shift end).
          if (!next || !next.isFree || next.startMins !== covered) break;
          covered = next.endMins;
          j++;
        }

        if (covered >= needUntil) {
          starts.push({
            range: `${blocks[i].startTime}-${fromMinutes(needUntil)}`,
            startTime: blocks[i].startTime,
            startMins: blocks[i].startMins,
          });
          // Next appointment can only begin once this one ends.
          i = j;
        }
      }

      return starts;
    }

    /** 570 -> "09:30", wrapping past midnight. */
    function fromMinutes(totalMinutes) {
      const hours = Math.floor(totalMinutes / 60) % 24;
      const minutes = totalMinutes % 60;
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }

    // ------------------------------------------------------------------------
    // Pet
    // ------------------------------------------------------------------------

    function patientOf(option) {
      if (!option || !option.dataset.patientData) return null;
      try {
        return JSON.parse(option.dataset.patientData);
      } catch (error) {
        return null;
      }
    }

    function selectedPatient() {
      return patientOf(petSelect.options[petSelect.selectedIndex]);
    }

    /** Saved pets with the journey's pet type first, then "Add New Pet". */
    function populatePets(preferredType) {
      resetSelect(petSelect, 'Choose Your Pet *');
      const patients = (!isUsingTemporaryPhone && clientData && Array.isArray(clientData.patients))
        ? clientData.patients.filter((patient) => patient && patient.PatientID && patient.PatientName)
        : [];

      const ofType = (patient) => petTypeFromSpecies(patient.Species && patient.Species.SpeciesName) === preferredType;
      patients
        .slice()
        .sort((a, b) => (ofType(b) ? 1 : 0) - (ofType(a) ? 1 : 0))
        .forEach((patient) => {
          const option = document.createElement('option');
          option.value = patient.PatientID;
          option.textContent = patient.PatientName;
          option.dataset.patientData = JSON.stringify(patient);
          petSelect.appendChild(option);
        });

      const addNew = document.createElement('option');
      addNew.value = 'new';
      addNew.textContent = 'Add New Pet';
      petSelect.appendChild(addNew);
      petSelect.disabled = false;

      return patients;
    }

    /**
     * Pick the pet on open (A2-A5): the saved pet, else the only pet of the journey's
     * type, else "Add New Pet" with that type when the customer has none of it.
     */
    function choosePetOnOpen(patients) {
      const type = booking.petType;
      const saved = booking.petId && booking.petId !== 'new'
        ? patients.find((patient) => String(patient.PatientID) === String(booking.petId))
        : null;

      if (saved) {
        petSelect.value = String(saved.PatientID);
      } else if (booking.petId === 'new' || isUsingTemporaryPhone) {
        petSelect.value = 'new';
      } else {
        const ofType = patients.filter((patient) => petTypeFromSpecies(patient.Species && patient.Species.SpeciesName) === type);
        if (ofType.length === 1) {
          petSelect.value = String(ofType[0].PatientID);
        } else if (ofType.length === 0 && type) {
          petSelect.value = 'new';
        } else {
          petSelect.value = '';
        }
      }

      return applyPetSelection({ fromOpen: true });
    }

    /** Show the pet fields for the current pet choice and return its pet type. */
    function applyPetSelection({ fromOpen } = {}) {
      const value = petSelect.value;
      isNewPet = value === 'new';

      if (isNewPet) {
        if (newPetDetails) newPetDetails.style.display = 'flex';
        if (newPetNameInput) {
          newPetNameInput.disabled = false;
          newPetNameInput.readOnly = false;
          if (fromOpen || !newPetNameInput.value) newPetNameInput.value = booking.petName || '';
        }
        if (newPetSpeciesSelect) {
          newPetSpeciesSelect.disabled = false;
          const speciesValue = booking.petType === 'Dog' ? '1' : booking.petType === 'Cat' ? '2' : '';
          if (fromOpen || !newPetSpeciesSelect.value) newPetSpeciesSelect.value = speciesValue;
        }
        booking.petId = 'new';
        return petTypeFromSpecies(newPetSpeciesSelect && newPetSpeciesSelect.value);
      }

      const patient = selectedPatient();
      if (patient) {
        const speciesName = (patient.Species && patient.Species.SpeciesName) || '';
        // Saved pet: show its name and species read-only.
        if (newPetDetails) newPetDetails.style.display = 'flex';
        if (newPetNameInput) {
          newPetNameInput.value = patient.PatientName || '';
          newPetNameInput.disabled = true;
          newPetNameInput.readOnly = true;
        }
        if (newPetSpeciesSelect) {
          newPetSpeciesSelect.value = petTypeFromSpecies(speciesName) === 'Dog' ? '1' : petTypeFromSpecies(speciesName) === 'Cat' ? '2' : '';
          newPetSpeciesSelect.disabled = true;
        }
        booking.petId = String(patient.PatientID);
        return petTypeFromSpecies(speciesName);
      }

      if (newPetDetails) newPetDetails.style.display = 'none';
      booking.petId = '';
      return booking.petType;
    }

    /** Pet or new-pet species changed. Same type: nothing below reloads (B1). */
    function handlePetChange() {
      setStatus('pet', null);
      const previousType = booking.petType;
      const newType = applyPetSelection();

      if (!newType && petSelect.value && !isNewPet) {
        // A rabbit or bird: there are no packages for it, so nothing below can be booked.
        setStatus('pet', 'info', 'We can only book dogs and cats online. Please choose another pet.');
        if (previousType) {
          booking.petType = '';
          refreshFrom(LEVEL.packages);
          return;
        }
      }
      if (newType && newType !== previousType) {
        booking.petType = newType;
        refreshFrom(LEVEL.packages);
        return;
      }
      // Another pet of the same type has its own bookings in the bag, so its free times differ.
      rerenderTimes();
      saveBooking();
      checkFormCompletion();
    }

    // ------------------------------------------------------------------------
    // City and centre
    // ------------------------------------------------------------------------

    /**
     * Cities come from the Zigly stores in Shopify, so the list shows even when VetBuddy
     * is down; each city's clinics come from VetBuddy, because bookings need its clinic IDs.
     * VetBuddy files some stores under another city (Secunderabad under Hyderabad), so a
     * store's own city also gets the clinic with its ID when their names share a word.
     */
    function buildCityGroups(clinics) {
      const groups = new Map();
      const groupFor = (cityName) => {
        const key = cityKey(cityName);
        if (!key) return null;
        if (!groups.has(key)) groups.set(key, { key, names: new Map(), clinics: [] });
        const group = groups.get(key);
        const name = String(cityName).trim();
        if (!group.names.has(name.toLowerCase())) group.names.set(name.toLowerCase(), name);
        return group;
      };
      const addClinic = (group, clinic) => {
        if (group && !group.clinics.some((item) => String(item.ClinicId) === String(clinic.ClinicId))) {
          group.clinics.push(clinic);
        }
      };

      stores.forEach((store) => groupFor(store.city));
      if (clinicsUnavailable) {
        // VetBuddy could not be reached: list the city's stores from Shopify so the
        // customer can still pick one. Doctors and slots need VetBuddy, so the booking
        // itself waits until it answers; a store without an ID can't be booked at all.
        stores.forEach((store) => {
          const name = (store.storeDetailPageTitle || store.name || '').trim();
          if (!name) return;
          addClinic(groups.get(cityKey(store.city)), {
            ClinicId: store.storeId ? String(store.storeId) : `store:${store.storeDetailsUrl || name}`,
            ClinicName: name,
            ClinicCity: store.city,
            fromShopify: true
          });
        });
      }
      clinics.forEach((clinic) => {
        if (clinic.ClinicCity && clinic.ClinicId) addClinic(groupFor(clinic.ClinicCity), clinic);
      });
      stores.forEach((store) => {
        if (!store.storeId || !cityKey(store.city)) return;
        const clinic = clinics.find((item) => String(item.ClinicId) === String(store.storeId));
        if (clinic && cityKey(clinic.ClinicCity) !== cityKey(store.city) &&
            sharedNameWords(store.storeDetailPageTitle || store.name, clinic.ClinicName) > 0) {
          addClinic(groups.get(cityKey(store.city)), clinic);
        }
      });

      groups.forEach((group) => {
        group.label = Array.from(group.names.values()).sort((a, b) => a.length - b.length).join(' / ');
        group.clinics.sort((a, b) => a.ClinicName.localeCompare(b.ClinicName));
      });
      return new Map(Array.from(groups.entries()).sort((a, b) => a[1].label.localeCompare(b[1].label)));
    }

    function populateCities() {
      const items = Array.from(cityGroups.values()).map((group) => ({ value: group.key, label: group.label }));
      fillSelect(citySelect, 'Select City *', items, booking.city);
      citySelect.disabled = items.length === 0;
    }

    /**
     * City and clinic for a Shopify store. A store whose city VetBuddy doesn't list
     * (Secunderabad is filed under Hyderabad) is placed by its clinic: same ID and a
     * shared name word, so a reused ID from another city never moves the customer.
     */
    function placeStore(store) {
      if (!store) return { city: '', centreId: '' };
      const findIn = (group) => {
        const byId = group.clinics.find((clinic) => String(clinic.ClinicId) === String(store.storeId || ''));
        if (byId) return byId;
        let best = null;
        let bestScore = 0;
        group.clinics.forEach((clinic) => {
          const score = sharedNameWords(store.storeDetailPageTitle || store.name, clinic.ClinicName);
          if (score > bestScore) {
            best = clinic;
            bestScore = score;
          }
        });
        return best;
      };

      const group = cityGroups.get(cityKey(store.city));
      if (group) {
        const clinic = findIn(group);
        return { city: group.key, centreId: clinic ? String(clinic.ClinicId) : '' };
      }

      for (const other of cityGroups.values()) {
        const clinic = other.clinics.find((item) => String(item.ClinicId) === String(store.storeId || ''));
        if (clinic && sharedNameWords(store.storeDetailPageTitle || store.name, clinic.ClinicName) > 0) {
          return { city: other.key, centreId: String(clinic.ClinicId) };
        }
      }
      return { city: '', centreId: '', unknownCity: store.city || '' };
    }

    function selectedClinic() {
      const group = cityGroups.get(booking.city);
      return group ? group.clinics.find((clinic) => String(clinic.ClinicId) === String(booking.centreId)) || null : null;
    }

    function serviceLabel(serviceName) {
      const service = services.find((item) => item.value === serviceName);
      return service ? service.label : serviceName;
    }

    /** City -> centre list (C1, C2). Returns false when no centre is chosen. */
    function stepCentres() {
      const group = cityGroups.get(booking.city);
      if (!group || !group.clinics.length) {
        resetSelect(clinicSelect, 'Choose a clinic *');
        if (group) setStatus('centre', 'info', `We can't take bookings in ${group.label} online yet. Please choose another city.`);
        if (booking.centreId) booking.centreId = '';
        return false;
      }
      if (clinicsUnavailable) {
        setStatus('centre', 'info', 'Live clinic details could not be loaded, so these are our stores.', 'clients');
      }

      const items = group.clinics.map((clinic) => ({ value: String(clinic.ClinicId), label: clinic.ClinicName }));
      const hadCentre = booking.centreId;
      let kept = fillSelect(clinicSelect, 'Choose a clinic *', items, booking.centreId);
      if (!kept && items.length === 1) {
        clinicSelect.value = items[0].value;
        kept = true;
      }
      booking.centreId = kept ? clinicSelect.value : '';
      if (hadCentre && !kept) {
        setStatus('centre', 'info', 'Your earlier clinic is not in this city. Please choose a clinic.');
      }

      // C2: say so when no centre in the city offers the chosen service.
      if (booking.service) {
        const offers = group.clinics.map((clinic) => clinicOffersService(clinic, booking.service));
        if (offers.length && offers.every((offer) => offer === false)) {
          setStatus('city', 'info', `${serviceLabel(booking.service)} is not available in ${group.label} yet. Try another city or service.`);
        }
      }
      return !!booking.centreId;
    }

    // ------------------------------------------------------------------------
    // Service and package
    // ------------------------------------------------------------------------

    /** Services the selected centre offers (C3, C4). */
    function stepServices() {
      const clinic = selectedClinic();
      const offered = services.filter((service) => !clinic || clinicOffersService(clinic, service.value) !== false);
      const hadService = booking.service;
      let kept = fillSelect(serviceTypeSelect, 'Select Service *', offered, booking.service);
      if (!kept && offered.length === 1) {
        serviceTypeSelect.value = offered[0].value;
        kept = true;
      }
      booking.service = kept ? serviceTypeSelect.value : '';
      if (hadService && booking.service !== hadService) {
        setStatus('service', 'info', `${serviceLabel(hadService)} is not offered at ${clinic ? clinic.ClinicName : 'this clinic'}. Please choose another service.`);
      }
      updateStaffLabel();
      form.dataset.serviceType = booking.service;
    }

    function updateStaffLabel() {
      const isGroomer = staffCategoryFor(booking.service) === 'groomer';
      const placeholder = doctorSelect.options[0];
      if (placeholder && !placeholder.value) {
        placeholder.textContent = booking.service ? (isGroomer ? 'Select Groomer *' : 'Select Doctor *') : 'Select Doctor/Groomer *';
      }
      const label = doctorSelect.closest('.available-service-drawer__form-group')?.querySelector('.available-service-drawer__label');
      if (label && label.firstChild) {
        label.firstChild.textContent = booking.service ? (isGroomer ? 'Select Groomer ' : 'Select Doctor ') : 'Select Doctor/Groomer ';
      }
    }

    // Unmapped packages are reported once per page, not on every reload of the list.
    const reportedUnmapped = new Set();

    /**
     * Packages for the service and pet type that have a VetBuddy reason for visit (D3, D4).
     * Returns false when no package is chosen.
     */
    async function stepPackages(alive) {
      const hadPackageId = booking.packageId;
      resetSelect(packageSelect, 'Select Package *');
      packagesForPet = [];

      if (!booking.service || !booking.petType) {
        if (booking.service && !booking.petType) {
          packageSelect.options[0].textContent = 'Choose your pet first';
        }
        clearSelectedPackage();
        return false;
      }

      // The packages are the Shopify cards on this page. VetBuddy's reason list only checks
      // each package's reason_for_visit and adds its name and duration.
      setStatus('package', 'loading', 'Loading packages...');
      const reasons = await fetchReasonsForVisit(booking.petType === 'Cat' ? 'Feline' : 'Canine');
      if (!alive()) return false;
      setStatus('package', null);

      // VetBuddy unreachable: a metafield holding a ReasonForVisitID is still a usable
      // reason, it just can't be checked; a text metafield can't be matched without the list.
      const reasonFor = (item) => {
        if (reasons) return matchReasonWithMetafield(item.reasonKey, reasons);
        if (!/^[0-9]+$/.test(item.reasonKey)) return null;
        return { ReasonForVisitID: item.reasonKey, ReasonForVisitName: item.title, TimeTakenMins: '', unverified: true };
      };
      if (!reasons) {
        console.warn('[Zigly booking] VetBuddy reasons unavailable; using package reason IDs unchecked', { petType: booking.petType });
      }

      packagesForPet = catalog
        .filter((item) => item.service === booking.service && item.petType === booking.petType)
        .map((item) => ({ ...item, reason: reasonFor(item) }))
        .filter((item) => {
          if (item.reason) return true;
          // D4: never send VetBuddy a blank reason; hide the package and tell the team.
          if (reasons && !reportedUnmapped.has(item.variantId)) {
            reportedUnmapped.add(item.variantId);
            console.warn('[Zigly booking] Package hidden: no VetBuddy reason for visit mapped', {
              package: item.title,
              variantId: item.variantId,
              petType: item.petType,
              service: item.service,
              reasonForVisitMetafield: item.reasonKey
            });
          }
          return false;
        });

      if (!packagesForPet.length) {
        packageSelect.options[0].textContent = 'No packages available';
        clearSelectedPackage();
        setStatus('package', 'info', `No ${serviceLabel(booking.service)} packages for ${booking.petType.toLowerCase()}s yet. Try another service.`);
        return false;
      }

      const items = packagesForPet.map((item) => ({
        value: item.variantId,
        label: item.priceText ? `${item.title} - ${item.priceText}` : item.title
      }));
      const previous = catalog.find((item) => item.variantId === hadPackageId);
      let kept = fillSelect(packageSelect, 'Select Package *', items, hadPackageId);

      // B2: switching Dog <-> Cat keeps the package when the other pet has the same one.
      if (!kept && previous) {
        const twin = packagesForPet.find((item) => packageFamily(item.title) === packageFamily(previous.title));
        if (twin) {
          packageSelect.value = twin.variantId;
          kept = true;
        }
      }
      if (!kept && packagesForPet.length === 1) {
        packageSelect.value = packagesForPet[0].variantId;
        kept = true;
      }

      if (kept) {
        applyPackage(packagesForPet.find((item) => item.variantId === packageSelect.value));
      } else {
        clearSelectedPackage();
        if (previous) {
          setStatus('package', 'info', previous.petType !== booking.petType
            ? `${previous.title} is not available for ${booking.petType.toLowerCase()}s. Please choose a package.`
            : 'This option is no longer available, please choose again.');
        } else if (hadPackageId) {
          setStatus('package', 'info', 'This option is no longer available, please choose again.');
        }
      }
      return !!selectedPackage;
    }

    function applyPackage(item) {
      selectedPackage = item || null;
      matchedReasonData = item ? item.reason : null;
      booking.packageId = item ? item.variantId : '';
      booking.reasonForVisitId = matchedReasonData ? String(matchedReasonData.ReasonForVisitID) : '';

      form.dataset.variantId = item ? item.variantId : '';
      form.dataset.productId = item ? item.productId : '';
      form.dataset.productTitle = item ? item.title : '';
      form.dataset.reasonForVisit = item ? item.reasonKey : '';
      form.dataset.serviceDuration = item ? item.duration : '';
      if (reasonForVisitInput) reasonForVisitInput.value = item ? item.reasonKey : '';

      if (packageInfo) {
        const advance = item && item.advanceText ? `${item.advanceText}, pay the rest at the centre.` : '';
        packageInfo.textContent = advance;
        packageInfo.classList.toggle('hidden', !advance);
      }
      updateDurationBox();
    }

    function clearSelectedPackage() {
      applyPackage(null);
    }

    // ------------------------------------------------------------------------
    // Doctor, date and slot
    // ------------------------------------------------------------------------

    function resetDoctor() {
      resetSelect(doctorSelect, 'Select Doctor/Groomer *');
      updateStaffLabel();
    }

    function staffName(staff) {
      return `${staff.FirstName} ${staff.LastName}`.trim();
    }

    function dateLimits() {
      const today = new Date();
      const last = new Date();
      last.setDate(today.getDate() + bookingWindowDays);
      return { min: toDateValue(today), max: toDateValue(last) };
    }

    function resetSlots(placeholder) {
      resetSelect(timeSlotSelect, placeholder || 'Select time slot *');
      if (timeSlotLoader) timeSlotLoader.style.display = 'none';
      lastStaffSlots = null;
      slotStaff = new Map();
      slotsLoading = false;
    }

    /**
     * Date -> the times any doctor/groomer at the centre is free for the whole package
     * (E1-E5). The staff list comes first and then every member's slots load together:
     * VetBuddy's all-staff slot call takes seconds and fails outright for some clinics.
     */
    async function stepSlots(alive) {
      setStatus('slot', null);
      const { min, max } = dateLimits();
      dateInput.min = min;
      dateInput.max = max;
      const fromShopifyOnly = String(booking.centreId).startsWith('store:');
      const canPickDate = !!(booking.centreId && booking.service && matchedReasonData && !fromShopifyOnly);
      dateInput.disabled = !canPickDate;
      if (booking.date && (booking.date < min || booking.date > max)) {
        booking.date = '';
        booking.slot = '';
      }
      dateInput.value = canPickDate ? booking.date : '';
      if (booking.centreId && booking.service && !fromShopifyOnly && !matchedReasonData) {
        resetSlots('Choose a package first');
      } else {
        resetSlots(canPickDate && !booking.date ? 'Pick a date first' : undefined);
      }

      if (fromShopifyOnly && booking.service) {
        setStatus('slot', 'info', 'Online booking is not open for this store yet. Please call the store or choose another clinic.');
        return;
      }
      if (!booking.petType || !canPickDate || !booking.date) return;

      const category = staffCategoryFor(booking.service);
      setStatus('slot', 'loading', 'Loading time slots...');
      timeSlotSelect.innerHTML = '<option value="">Loading time slots...</option>';
      if (timeSlotLoader) timeSlotLoader.style.display = 'inline-block';

      const staffResult = await fetchStaff(booking.centreId, category);
      if (!alive()) return;
      if (!staffResult.ok) {
        resetSlots();
        setStatus('slot', 'error', `Could not load ${category === 'groomer' ? 'groomers' : 'doctors'}.`, LEVEL.slots);
        return;
      }
      if (!staffResult.staff.length) {
        resetSlots('No slots available');
        setStatus('slot', 'info', `No ${category} is available at this clinic for ${serviceLabel(booking.service)}. Please try another clinic.`);
        booking.slot = '';
        return;
      }

      // Times show as each doctor/groomer's slots arrive: VetBuddy takes up to ~8 s for some
      // long-serving staff, and the others' times should not wait for them. One lookup
      // failing costs only that person's times, not the whole list.
      // Kept in VetBuddy's staff order, whatever order the answers arrive in.
      const answers = new Array(staffResult.staff.length).fill(undefined);
      const loadedNow = () => answers.filter(Boolean);
      const missing = [];
      slotsLoading = true;
      await Promise.all(staffResult.staff.map((staff, index) =>
        fetchSlotsString(booking.centreId, staff.StaffID, booking.date, false)
          .then((slots) => {
            answers[index] = { staff, slots };
          })
          .catch(() => {
            answers[index] = null;
            missing.push(staff.StaffID);
          })
          .finally(() => {
            if (!alive() || !loadedNow().length || !answers.includes(undefined)) return;
            if (timeSlotLoader) timeSlotLoader.style.display = 'none';
            renderSlots(loadedNow());
            stepDoctors();
            checkFormCompletion();
          })
      ));
      if (!alive()) return;
      slotsLoading = false;
      const loaded = loadedNow();
      if (!loaded.length) {
        resetSlots();
        setStatus('slot', 'error', 'Could not load time slots.', LEVEL.slots);
        return;
      }
      if (missing.length) {
        console.warn('[Zigly booking] Some staff slots did not load; their times are left out', {
          clinicId: booking.centreId,
          date: booking.date,
          missing
        });
      }
      if (timeSlotLoader) timeSlotLoader.style.display = 'none';
      setStatus('slot', null);
      renderSlots(loaded);
    }

    /**
     * The time list: every start at least one doctor/groomer is free for. Each time
     * remembers who is free for it, for the doctor/groomer list below.
     */
    function renderSlots(staffSlots) {
      lastStaffSlots = staffSlots;
      slotStaff = new Map();
      const bagHeldRanges = new Set();
      staffSlots.forEach(({ staff, slots }) => {
        bookableSlots(slots, booking.date, staff.StaffID).forEach((block) => {
          if (!slotStaff.has(block.range)) slotStaff.set(block.range, { ...block, staff: [] });
          slotStaff.get(block.range).staff.push(staff);
        });
        bookableSlots(slots, booking.date, staff.StaffID, true).forEach((block) => bagHeldRanges.add(block.range));
      });
      const starts = Array.from(slotStaff.values()).sort((a, b) => a.startMins - b.startMins);
      const bagHeld = Array.from(bagHeldRanges).filter((range) => !slotStaff.has(range)).length;

      if (!starts.length && slotsLoading) {
        // The doctors/groomers still loading may have times yet.
        timeSlotSelect.innerHTML = '<option value="">Loading time slots...</option>';
        timeSlotSelect.disabled = true;
        return;
      }
      if (!starts.length) {
        resetSlots('No slots available');
        lastStaffSlots = staffSlots;
        setStatus('slot', 'info', bagHeld
          ? 'The free times on this date clash with a booking already in your bag. Please pick another day.'
          : 'No slots on this date, please pick another day.');
        if (booking.slot) booking.slot = '';
        return;
      }

      const hadSlot = booking.slot;
      const kept = fillSelect(
        timeSlotSelect,
        'Select time slot *',
        starts.map((block) => ({ value: block.range, label: block.startTime, data: { startTime: block.startTime } })),
        booking.slot
      );
      if (slotsLoading) {
        // A saved time may belong to a doctor/groomer still loading, so it is kept for now.
        if (kept) booking.slot = timeSlotSelect.value;
        setStatus('slot', 'loading', 'Loading more times...');
        return;
      }
      booking.slot = kept ? timeSlotSelect.value : '';
      if (hadSlot && !kept) {
        setStatus('slot', 'info', 'Your earlier time is no longer free, please pick another.');
      } else if (bagHeld) {
        setStatus('slot', 'info', 'Times that clash with a booking already in your bag are not shown.');
      } else {
        setStatus('slot', null);
      }
    }

    /** Rebuild the times and the doctor/groomer list from the slots already loaded. */
    function rerenderTimes() {
      if (!lastStaffSlots) return;
      renderSlots(lastStaffSlots);
      stepDoctors();
    }

    /**
     * Time -> the doctors/groomers free for the whole of it. When only one is free they
     * are chosen for the customer. A doctor chosen earlier is kept while they are free.
     */
    function stepDoctors() {
      setStatus('doctor', null);
      resetDoctor();
      const free = booking.slot && slotStaff.get(booking.slot);
      if (!free) {
        // Keep doctorId: that doctor is chosen again at a time they are free.
        if (lastStaffSlots) doctorSelect.options[0].textContent = 'Pick a time first';
        return false;
      }

      const word = staffCategoryFor(booking.service) === 'groomer' ? 'groomer' : 'doctor';
      const items = free.staff.map((staff) => ({
        value: String(staff.StaffID),
        label: staffName(staff) + (staff.StaffCategoryName ? ` (${staff.StaffCategoryName})` : ''),
        data: { staffData: JSON.stringify(staff) }
      }));
      const hadDoctor = booking.doctorId;
      let kept = fillSelect(doctorSelect, doctorSelect.options[0].textContent, items, booking.doctorId);
      if (!kept && items.length === 1) {
        doctorSelect.value = items[0].value;
        kept = true;
      }
      booking.doctorId = kept ? doctorSelect.value : '';
      if (hadDoctor && booking.doctorId !== hadDoctor) {
        setStatus('doctor', 'info', items.length === 1
          ? `Your earlier ${word} is not free at this time, so ${staffName(free.staff[0])} is selected.`
          : `Your earlier ${word} is not free at this time, please choose another.`);
      }
      return !!booking.doctorId;
    }

    /** "Bruno already has Full Grooming at 11:30 on 28/10/2026 in your bag..." */
    function clashMessage(clash) {
      const props = clash.props;
      const when = `${String(props._timeSlot).split('-')[0]} on ${String(props._appointmentDate).split('-').reverse().join('/')}`;
      const samePet = isSamePet(props, currentPetIdentity());
      return samePet
        ? `${props._patientName || 'Your pet'} already has ${clash.title} at ${when} in your bag. Please pick another time.`
        : `This ${staffCategoryFor(booking.service) === 'groomer' ? 'groomer' : 'doctor'} is already booked at ${when} for ${clash.title} in your bag. Please pick another time.`;
    }

    // ------------------------------------------------------------------------
    // The cascade
    // ------------------------------------------------------------------------

    /** Disable everything below the field being fetched until it returns (G4). */
    function lockBelow(level) {
      if (level < LEVEL.centre) clinicSelect.disabled = true;
      if (level < LEVEL.service) serviceTypeSelect.disabled = true;
      if (level < LEVEL.packages) packageSelect.disabled = true;
      if (level < LEVEL.slots) dateInput.disabled = true;
      if (level <= LEVEL.slots) timeSlotSelect.disabled = true;
      doctorSelect.disabled = true;
      submitButton.disabled = true;
    }

    /**
     * Reload the fields from `level` down. Each field keeps its value when it is still
     * in the new list. A newer run makes older ones drop their answers (E7).
     */
    async function refreshFrom(level) {
      const run = ++runSeq;
      const alive = () => run === runSeq && drawerElement.isConnected;
      lockBelow(level);

      try {
        if (level <= LEVEL.city) stepCentres();
        if (level <= LEVEL.centre) stepServices();
        // The staff list needs only the clinic and service, so it loads while the packages do.
        if (level <= LEVEL.packages && booking.centreId && booking.service && !String(booking.centreId).startsWith('store:')) {
          fetchStaff(booking.centreId, staffCategoryFor(booking.service));
        }
        if (level <= LEVEL.packages) {
          await stepPackages(alive);
          if (!alive()) return;
        }
        if (level <= LEVEL.slots) {
          await stepSlots(alive);
          if (!alive()) return;
        }
        stepDoctors();
      } finally {
        if (alive()) {
          saveBooking();
          checkFormCompletion();
        }
      }
    }

    // ------------------------------------------------------------------------
    // Cart
    // ------------------------------------------------------------------------

    /**
     * The service bookings already in the bag, one entry per unit (a line of quantity 2
     * is two bookings). Read on drawer open and again before submit; [] when the cart
     * can't be read, so a cart outage never blocks a booking.
     */
    async function loadCartServiceLines() {
      try {
        const response = await fetch('/cart.js', { credentials: 'same-origin' });
        const cartData = await response.json();
        cartServiceLines = [];
        (cartData.items || []).forEach((item) => {
          const props = item.properties || {};
          if (props._productType !== 'Service') return;
          for (let unit = 0; unit < Math.max(1, item.quantity || 1); unit++) {
            cartServiceLines.push({ title: props._packageName || item.product_title, props });
          }
        });
        rememberCartPatients();
      } catch (error) {
        cartServiceLines = [];
      }
      return cartServiceLines;
    }

    /**
     * A pet registered for an earlier booking in the bag keeps its VetBuddy ID, so the
     * next booking for it reuses that ID instead of registering the pet again - even
     * after a refresh, when VetBuddy's lookup may not list the new pet yet.
     */
    function rememberCartPatients() {
      (cartServiceLines || []).forEach(({ props }) => {
        if (!props._clientId || !props._patientId || !props._patientName) return;
        const petKey = `${props._clientId}|${String(props._patientName).trim().toLowerCase()}|${normalizeSpeciesForApi(props._species)}`;
        if (!createdPatientIds[petKey]) createdPatientIds[petKey] = String(props._patientId);
      });
    }

    function serviceLimitReached() {
      return !!cartServiceLines && cartServiceLines.length >= maxServicesPerOrder;
    }

    function serviceLimitMessage() {
      return String(errorMessages.serviceLimit || '').replace(/\[max\]/g, String(maxServicesPerOrder));
    }

    /** The pet this booking is for, as the cart lines record it. */
    function currentPetIdentity() {
      const petData = getSelectedPetData();
      return {
        patientId: isNewPet ? '' : String(petSelect.value || ''),
        name: String(petData.patientName || '').trim().toLowerCase(),
        petType: booking.petType
      };
    }

    function isSamePet(props, pet) {
      if (pet.patientId && props._patientId) return String(props._patientId) === pet.patientId;
      return !!pet.name &&
        String(props._patientName || '').trim().toLowerCase() === pet.name &&
        (!props._petType || !pet.petType || props._petType === pet.petType);
    }

    /** [start, end) minutes of a "11:30-12:00" range; null when unparseable. */
    function rangeMinutes(range) {
      const [startText, endText] = String(range || '').split('-');
      const start = toMinutes(startText);
      let end = toMinutes(endText);
      if (start === null || end === null) return null;
      if (end <= start) end += 1440;
      return { start, end };
    }

    /**
     * The booking in the bag that a start at `startTime` would clash with, else null.
     * VetBuddy only learns of a booking after payment, so its slots still show the
     * times this bag holds. A clash is the same doctor/groomer at the same centre, or
     * the same pet anywhere, at overlapping times on the same date.
     */
    function clashingCartBooking(startTime, staffId = booking.doctorId) {
      if (!cartServiceLines || !cartServiceLines.length || !booking.date) return null;
      const start = toMinutes(startTime);
      if (start === null) return null;
      const end = start + (resolveServiceDurationMins() || 10);
      const pet = currentPetIdentity();

      return cartServiceLines.find(({ props }) => {
        if (props._appointmentDate !== booking.date) return false;
        const held = rangeMinutes(props._timeSlot);
        if (!held || held.end <= start || held.start >= end) return false;
        const sameStaff = String(props._clinicId) === String(booking.centreId) &&
          String(props._staff_id) === String(staffId);
        return sameStaff || isSamePet(props, pet);
      }) || null;
    }

    /** The note above Add To Bag, shown only once the bag holds the most services one order can. */
    function updateCartServiceMessage() {
      if (!cartServiceMessage) return;
      if (serviceLimitReached()) {
        cartServiceMessage.innerHTML = serviceLimitMessage();
      } else {
        cartServiceMessage.textContent = '';
      }
      cartServiceMessage.classList.toggle('hidden', !cartServiceMessage.textContent.trim());
      checkFormCompletion();
    }

    /**
     * Show error message (supports rich text/HTML)
     * Automatically hides after 4 seconds
     */
    function showError(message) {
      if (errorContainer) {
        errorContainer.innerHTML = message;
        errorContainer.style.display = 'block';

        // Clear any existing timeout
        if (errorContainer._errorTimeout) {
          clearTimeout(errorContainer._errorTimeout);
        }

        // Auto-hide after 4 seconds
        errorContainer._errorTimeout = setTimeout(() => {
          clearError();
        }, 4000);
      }
    }

    /**
     * Clear error message
     */
    function clearError() {
      if (errorContainer) {
        // Clear any existing timeout
        if (errorContainer._errorTimeout) {
          clearTimeout(errorContainer._errorTimeout);
          errorContainer._errorTimeout = null;
        }
        errorContainer.textContent = '';
        errorContainer.style.display = 'none';
      }
    }

    /**
     * Add to Bag is enabled only with every field filled and Terms ticked (F1).
     */
    function checkFormCompletion() {
      if (!submitButton) return false;

      const isValid = !!(
        petSelect.value &&
        booking.petType &&
        (!isNewPet || (newPetNameInput && newPetNameInput.value.trim() && newPetSpeciesSelect && newPetSpeciesSelect.value)) &&
        citySelect.value &&
        clinicSelect.value &&
        serviceTypeSelect.value &&
        selectedPackage && matchedReasonData &&
        doctorSelect.value &&
        dateInput.value &&
        timeSlotSelect.value &&
        termsCheckbox.checked &&
        !serviceLimitReached() &&
        !isSubmitting
      );

      // Incomplete only looks disabled: a tap still reaches handleFormSubmit, which points
      // the customer to what is missing instead of leaving a grey button with no reason.
      submitButton.disabled = isSubmitting;
      submitButton.classList.toggle('is-incomplete', !isValid && !isSubmitting);
      submitButton.setAttribute('aria-disabled', String(!isValid));
      return isValid;
    }

    /** Scroll to the first field Add to Bag still needs and say what it needs. */
    function showMissingField() {
      if (serviceLimitReached()) {
        showError(serviceLimitMessage());
        return;
      }
      const word = staffCategoryFor(booking.service) === 'groomer' ? 'groomer' : 'doctor';
      const missing = [
        [!petSelect.value, petSelect, 'pet', 'Please choose your pet.'],
        [isNewPet && !(newPetNameInput && newPetNameInput.value.trim()), newPetNameInput, 'pet', "Please enter your pet's name."],
        [isNewPet && !(newPetSpeciesSelect && newPetSpeciesSelect.value), newPetSpeciesSelect, 'pet', "Please choose your pet's species."],
        [!booking.petType, petSelect, 'pet', 'We can only book dogs and cats online. Please choose another pet.'],
        [!citySelect.value, citySelect, 'city', 'Please choose a city.'],
        [!clinicSelect.value, clinicSelect, 'centre', 'Please choose a clinic.'],
        [!serviceTypeSelect.value, serviceTypeSelect, 'service', 'Please choose a service.'],
        [!(selectedPackage && matchedReasonData), packageSelect, 'package', 'Please choose a package.'],
        [!dateInput.value, dateInput, 'date', 'Please pick a date.'],
        [!timeSlotSelect.value, timeSlotSelect, 'slot', 'Please pick a time.'],
        [!doctorSelect.value, doctorSelect, 'doctor', `Please choose a ${word}.`],
        [!termsCheckbox.checked, termsCheckbox, null, 'Please accept the Terms & Conditions to continue.']
      ].find(([isMissing]) => isMissing);
      if (!missing) {
        showError('Please fill all required fields.');
        return;
      }

      const [, control, field, message] = missing;
      const group = control && (control.closest('.available-service-drawer__form-group') || control);
      if (group) group.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // The terms line has no status slot of its own; the error box above the form is in view.
      if (field) {
        setStatus(field, 'info', message);
      } else {
        showError(message);
      }
      if (control && !control.disabled) control.focus({ preventScroll: true });
    }

    /**
     * Get selected pet data
     */
    function getSelectedPetData() {
      if (isNewPet) {
        return {
          patientName: newPetNameInput ? newPetNameInput.value.trim() : '',
          species: newPetSpeciesSelect ? newPetSpeciesSelect.options[newPetSpeciesSelect.selectedIndex].text : ''
        };
      }
      const patient = selectedPatient();
      if (patient) {
        return {
          patientName: patient.PatientName || '',
          species: patient.Species?.SpeciesName || ''
        };
      }
      return { patientName: '', species: '' };
    }

    /**
     * Calculate time slot with duration from API
     */
    function calculateTimeSlot(timeRange) {
      // If no range or no API duration, return original range
      if (!timeRange || !matchedReasonData || !matchedReasonData.TimeTakenMins) {
        return timeRange;
      }

      const duration = parseInt(matchedReasonData.TimeTakenMins);
      if (isNaN(duration) || duration <= 0) {
        return timeRange;
      }

      // Parse the start time (e.g., "12:00" from "12:00-12:15")
      const startTimeStr = timeRange.split('-')[0].trim();
      const timeParts = startTimeStr.split(':');
      if (timeParts.length !== 2) return timeRange;

      let hours = parseInt(timeParts[0]);
      let minutes = parseInt(timeParts[1]);

      if (isNaN(hours) || isNaN(minutes)) return timeRange;

      // Add the duration minutes
      let totalMinutes = hours * 60 + minutes + duration;

      let endHours = Math.floor(totalMinutes / 60) % 24;
      let endMinutes = totalMinutes % 60;

      // Format end time as HH:mm with leading zeros
      const formattedEndHours = String(endHours).padStart(2, '0');
      const formattedEndMinutes = String(endMinutes).padStart(2, '0');
      const endTimeStr = `${formattedEndHours}:${formattedEndMinutes}`;

      // Return the new range (e.g., "12:00-12:30")
      return `${startTimeStr}-${endTimeStr}`;
    }

    /**
     * The VetBuddy client and patient IDs for this booking, registering either if it is missing.
     * Returns empty strings rather than failing the add to bag: a booking with names but no IDs
     * can still be fixed by hand, where a lost sale cannot.
     */
    async function ensureVetBuddyRecords(petData) {
      const ids = { clientId: '', patientId: '' };
      if (!window.ZiglyVetBuddy) return ids;

      try {
        const config = window.ZIGLY_BOOKING_CONFIG || {};
        const customer = config.customer || {};
        const clinicName = clinicSelect.options[clinicSelect.selectedIndex].textContent.trim();
        // customerPhone is local to the drawer-open handler; read it from where that handler stores it.
        const storedPhone = drawerElement.dataset.customerPhone || config.customerPhone || '';
        const phone = extractPhoneNumber(storedPhone) || '';

        ids.clientId = (clientData && clientData.ClientID) || (phone && createdClientIds[phone]) || '';
        // Guests have no phone, and VetBuddy keys a client on it; their booking goes
        // through without IDs.
        if (!ids.clientId && phone) {
          // No VetBuddy client for this phone (or the lookup failed). Registering is safe
          // either way: VetBuddy answers an existing phone with that client's ID. It refuses
          // a client without a first name, so fall back when the account has none.
          const created = await window.ZiglyVetBuddy.createClient({
            phone: phone,
            firstName: (clientData && clientData.FirstName) || customer.firstName || 'Zigly',
            lastName: (clientData && clientData.LastName) || customer.lastName || 'Customer',
            email: (clientData && clientData.Email) || customer.email || '',
            clinicName: clinicName,
            address1: customer.address1 || '',
            // The customer's own city when the account has an address, else the clinic's.
            city: customer.city || cityName(),
            zip: customer.zip || '',
            state: customer.state || '',
          });
          if (created.responseCode === 0 && created.data.clientId) {
            ids.clientId = created.data.clientId;
            createdClientIds[phone] = ids.clientId;
          }
        }

        // An existing pet already carries its id as the dropdown's value.
        ids.patientId = isNewPet ? '' : (petSelect.value || '');

        if (!ids.patientId && ids.clientId && petData.patientName) {
          const petKey = `${ids.clientId}|${petData.patientName.trim().toLowerCase()}|${normalizeSpeciesForApi(petData.species)}`;
          if (createdPatientIds[petKey]) {
            ids.patientId = createdPatientIds[petKey];
          } else {
            const created = await window.ZiglyVetBuddy.createPatient({
              clientId: ids.clientId,
              phone: phone,
              petName: petData.patientName.trim(),
              species: normalizeSpeciesForApi(petData.species),
              clinicName: clinicName,
            });
            if (created.responseCode === 0 && created.data.patientId) {
              ids.patientId = created.data.patientId;
              createdPatientIds[petKey] = ids.patientId;
            }
          }
        }
      } catch (error) {
        console.warn('[Zigly] VetBuddy records not registered:', error.message);
      }

      if (!ids.clientId || !ids.patientId) {
        // The booking after payment needs both IDs; log what was sent so ops can fix it by hand.
        console.warn('[Zigly booking] Booking goes to the bag without VetBuddy IDs', {
          clientId: ids.clientId,
          patientId: ids.patientId,
          hasPhone: !!extractPhoneNumber(drawerElement.dataset.customerPhone || (window.ZIGLY_BOOKING_CONFIG || {}).customerPhone || ''),
          petName: petData.patientName,
          species: petData.species
        });
      }
      return ids;
    }

    /** The clinic's own city name ("Gurugram"), for the cart line and VetBuddy. */
    function cityName() {
      const clinic = selectedClinic();
      if (clinic && clinic.ClinicCity) return clinic.ClinicCity;
      const group = cityGroups.get(booking.city);
      return group ? group.label : '';
    }

    /**
     * Create pet profile in Shopify (for new pets)
     */
    async function createPetProfile(petName, species) {
      try {
        const customerIdElement = document.querySelector('[data-customer-id]');
        const customerId = customerIdElement
          ? customerIdElement.getAttribute('data-customer-id')
          : (window.shopifyCustomerId || (window.ZIGLY_BOOKING_CONFIG || {}).customerId || '');

        if (!customerId || customerId === '') {
          return null;
        }

        let petType = 'others';
        if (species.toLowerCase().includes('canine') || species.toLowerCase().includes('dog')) {
          petType = 'dog';
        } else if (species.toLowerCase().includes('feline') || species.toLowerCase().includes('cat')) {
          petType = 'cat';
        }

        let petData = {
          id: `${customerId}__${petName.replace(/\s+/g, '_')}`,
          type: petType,
          name: petName || "",
          breed: species || "",
          dateOfBirth: "",
          gender: "",
          vaccinationStatus: "no",
          dewormingStatus: "no",
          image_url: "https://zigly.com/cdn/shop/files/3_Dog_Avatar.png?v=1756191827",
        };

        const petPayload = {
          customerID: `gid://shopify/Customer/${customerId}`,
          pets: [petData]
        };

        if (typeof generateAESKey === 'undefined' || typeof encryptRSA === 'undefined' || typeof encryptAES === 'undefined') {
          return null;
        }

        const aesKey = await generateAESKey();
        const encryptedAESKey = await encryptRSA(aesKey);
        const { encryptedData, iv } = await encryptAES(petPayload, aesKey);

        const encryptedPetPayload = { encryptedAESKey, encryptedData, iv };

        const petResponse = await retryFetch(() =>
          fetch(
            'https://9bc3azl93i.execute-api.ap-south-1.amazonaws.com/dev/customer-edit?type=createCustomerPet',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(encryptedPetPayload),
            }
          )
        );

        const petResponseData = await petResponse.text();

        if (!petResponse.ok) {
          throw new Error(`Pet creation failed: ${petResponseData.message || 'Unknown error'}`);
        }

        return petResponseData;

      } catch (error) {
        return null;
      }
    }

    /**
     * Recheck the chosen slot against VetBuddy right before it goes to the bag (E8).
     * 'free', 'taken', or 'unknown' when VetBuddy could not be asked - an unanswered
     * recheck does not block the sale, the booking after payment checks again.
     */
    async function recheckSlot() {
      try {
        const slotsString = await fetchSlotsString(booking.centreId, booking.doctorId, booking.date, true);
        const free = bookableSlots(slotsString, booking.date, booking.doctorId).some((block) => block.range === booking.slot);
        return free ? 'free' : 'taken';
      } catch (error) {
        console.warn('[Zigly booking] Slot recheck failed, adding to bag anyway', error.message);
        return 'unknown';
      }
    }

    function setSubmitting(on) {
      isSubmitting = on;
      if (submitButtonText) submitButtonText.style.display = on ? 'none' : '';
      if (submitButtonSpinner) submitButtonSpinner.classList.toggle('hidden', !on);
      if (on) {
        submitButton.disabled = true;
      } else {
        checkFormCompletion();
      }
    }

    /**
     * Handle form submission
     */
    async function handleFormSubmit(e) {
      e.preventDefault();
      // F3: a second click while the first is still going adds nothing.
      if (isSubmitting) return;
      clearError();

      if (!checkFormCompletion()) {
        showMissingField();
        return;
      }

      setSubmitting(true);
      try {
        // The bag may have changed in another tab since the drawer opened.
        await loadCartServiceLines();
        updateCartServiceMessage();
        if (serviceLimitReached()) {
          showError(serviceLimitMessage());
          return;
        }
        const clash = clashingCartBooking(String(booking.slot).split('-')[0]);
        if (clash) {
          booking.slot = '';
          rerenderTimes();
          setStatus('slot', 'info', clashMessage(clash));
          return;
        }

        if (await recheckSlot() === 'taken') {
          const word = staffCategoryFor(booking.service) === 'groomer' ? 'groomer' : 'doctor';
          // Rebuild the times from the answer the recheck just cached. Another doctor/groomer
          // may still be free then, so the time stays when it can.
          await refreshFrom(LEVEL.slots);
          if (booking.slot) {
            setStatus('doctor', 'info', doctorSelect.value
              ? `Your ${word} was just booked at this time, so ${doctorSelect.options[doctorSelect.selectedIndex].textContent} is selected instead.`
              : `Your ${word} was just booked at this time. Please choose another ${word} or time.`);
          } else {
            setStatus('slot', 'info', 'That time was just booked by someone else. Please pick another slot.');
          }
          return;
        }

        // Get form data
        const petData = getSelectedPetData();
        const calculatedTimeSlot = calculateTimeSlot(timeSlotSelect.value);
        const appointmentTypeToSubmit = staffCategoryFor(booking.service) === 'groomer' ? 'Grooming' : 'OPD';

        // Make sure the pet parent and the pet exist in VetBuddy before the order is placed.
        // The appointment itself is booked after payment, from the order, so the IDs have to
        // travel with the line item - the middleware cannot resolve a pet from its name alone.
        const vetbuddyIds = await ensureVetBuddyRecords(petData);

        // Every final id goes on the cart line; the VetBuddy booking after payment reads
        // only these, never the journey data in the browser (F2, F4).
        const properties = {
          _productType: 'Service', // Identifier for service products
          _patientName: petData.patientName,
          _species: petData.species,
          _petType: booking.petType,
          _city: cityName(),
          _clinicId: booking.centreId,
          _clinicName: clinicSelect.options[clinicSelect.selectedIndex].textContent,
          _service: serviceLabel(booking.service),
          _packageName: selectedPackage.title,
          _reasonForVisitId: String(matchedReasonData.ReasonForVisitID),
          _serviceProductType: matchedReasonData.ReasonForVisitName || selectedPackage.title,
          _staff_id: booking.doctorId,
          _appointmentDate: booking.date,
          _timeSlot: calculatedTimeSlot,
          _appointmentType: appointmentTypeToSubmit,
          // Only real IDs go on the line. A signed-out customer has none yet, and the keys must be
          // left out rather than sent empty: before 9 Oct 2026 guest lines had no such keys and the
          // booking after payment registered the client and pet from the checkout phone.
          ...(vetbuddyIds.clientId ? { _clientId: vetbuddyIds.clientId } : {}),
          ...(vetbuddyIds.patientId ? { _patientId: vetbuddyIds.patientId } : {})
        };

        // Add to cart
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            items: [{
              id: selectedPackage.variantId,
              quantity: 1,
              properties: properties
            }],
            sections: ['cart-icon-bubble', 'cart-drawer']
          })
        });

        const result = await response.json();

        if (result.status) {
          showError(result.description || errorMessages.generic || 'Failed to add product to cart.');
          return;
        }

        // The appointment is in the bag now, so a later open starts with a fresh date and slot.
        booking.date = '';
        booking.slot = '';
        saveBooking();

        // Close the booking drawer before the cart drawer opens. openDrawer() locks scroll on
        // both <body> and <html>; unlocking only <body> left the page frozen after the add.
        const drawer = drawerElement.closest('.available-service-drawer');
        if (drawer) {
          if (typeof window.closeDrawer === 'function') {
            window.closeDrawer(drawer);
          } else {
            drawer.classList.remove('is-open');
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
          }
        }

        // Success - update cart drawer and bubble icon
        if (result.sections) {
          const parser = new DOMParser();

          // Update cart drawer
          if (result.sections['cart-drawer']) {
            try {
              const newCartDrawerHtml = parser.parseFromString(result.sections['cart-drawer'], 'text/html');
              const newCartDrawer = newCartDrawerHtml.querySelector('cart-drawer');

              if (newCartDrawer) {
                const oldCartDrawer = document.querySelector('cart-drawer');
                oldCartDrawer.classList.remove('is-empty');
                // Update the inner HTML of the cart drawer
                oldCartDrawer.innerHTML = newCartDrawer.innerHTML;
              }
            } catch (error) {
              // Error updating cart drawer
            }
          }

          // Update cart icon bubble
          if (result.sections['cart-icon-bubble']) {
            try {
              const newCartIconBubbleHtml = parser.parseFromString(result.sections['cart-icon-bubble'], 'text/html');
              // The response has a wrapper div, get the content inside it
              const sectionWrapper = newCartIconBubbleHtml.querySelector('#shopify-section-cart-icon-bubble');

              if (sectionWrapper) {
                const oldCartIconBubble = document.querySelector('#cart-icon-bubble');

                if (oldCartIconBubble) {
                  // Extract SVG from response
                  const newSvg = sectionWrapper.querySelector('svg');
                  const oldSvg = oldCartIconBubble.querySelector('svg');

                  if (newSvg && oldSvg) {
                    // Update SVG class: remove icon-cart-empty, add icon-cart
                    oldSvg.classList.remove('icon-cart-empty');
                    oldSvg.classList.add('icon-cart');
                  }

                  // Extract and update cart count bubble
                  const newCountBubble = sectionWrapper.querySelector('.cart-count-bubble');
                  const oldCountBubble = oldCartIconBubble.querySelector('.cart-count-bubble');

                  if (newCountBubble) {
                    if (oldCountBubble) {
                      // Update existing count bubble
                      oldCountBubble.innerHTML = newCountBubble.innerHTML;
                    } else {
                      // Add new count bubble if it doesn't exist
                      oldCartIconBubble.appendChild(newCountBubble.cloneNode(true));
                    }
                  }

                  // Update the "Bag" text span class
                  const newBagSpan = sectionWrapper.querySelector('.visually-hidden');
                  const oldBagSpan = oldCartIconBubble.querySelector('span.none-visually-hidden, span.visually-hidden');

                  if (newBagSpan && oldBagSpan) {
                    oldBagSpan.className = newBagSpan.className;
                    oldBagSpan.textContent = newBagSpan.textContent;
                  }
                }
              }
            } catch (error) {
              // Error updating cart icon bubble
            }
          }

          // Open cart drawer after updating
          setTimeout(() => {
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) {
              // Re-bind overlay click event to close drawer after content update
              const overlay = cartDrawer.querySelector('#CartDrawer-Overlay') || cartDrawer.querySelector('.cart-drawer__overlay');
              if (overlay) {
                // Add click listener to close drawer when overlay is clicked
                const closeHandler = function(e) {
                  // Only close if clicking directly on overlay, not on drawer content
                  if (e.target === overlay || e.target.closest('.cart-drawer__overlay') === overlay) {
                    if (cartDrawer.close && typeof cartDrawer.close === 'function') {
                      cartDrawer.close();
                    } else {
                      cartDrawer.classList.remove('active', 'animate');
                      document.body.classList.remove('overflow-hidden');
                    }
                  }
                };
                overlay.addEventListener('click', closeHandler);
              }

              // Use the open method if available, otherwise add classes
              if (cartDrawer.open && typeof cartDrawer.open === 'function') {
                cartDrawer.open();
              } else {
                cartDrawer.classList.add('animate', 'active');
                document.body.classList.add('overflow-hidden');
              }
            }
          }, 100);
        }

        // Create pet profile if it's a new pet
        if (isNewPet && petData.patientName && petData.patientName.trim() !== '') {
          try {
            await createPetProfile(petData.patientName, petData.species);
          } catch (error) {
            // Error creating pet profile (non-blocking)
          }
        }

      } catch (error) {
        showError(errorMessages.generic || 'Unable to add product to cart. Please try again.');
      } finally {
        setSubmitting(false);
      }
    }

    // ------------------------------------------------------------------------
    // Opening the drawer
    // ------------------------------------------------------------------------

    /**
     * What the page says right now: the store page the customer is on and the card
     * they clicked (service, pet type, package).
     */
    function readPageContext() {
      const card = Array.from(document.querySelectorAll('.available-service-card'))
        .find((item) => String(item.dataset.variantId) === String(productData.variantId || form.dataset.variantId));
      const slide = card ? card.closest('[data-role="product-slide"]') : null;
      const store = readSelectedStore();
      return {
        service: (form.dataset.serviceName || (slide && slide.dataset.serviceName) || '').trim(),
        petType: petTypeFromSpecies(form.dataset.packageSpecies || (slide && slide.dataset.species)),
        packageId: String(productData.variantId || form.dataset.variantId || ''),
        store,
        storeId: store ? String(store.storeId || store.storeDetailsUrl || '') : ''
      };
    }

    /**
     * Saved booking + page context. The store page and the card clicked win (A7), except
     * when they are the same store and card as last time - then the customer's own edits
     * in the drawer are restored (A9).
     */
    function buildBooking(saved, context) {
      const next = Object.assign(emptyBooking(), saved || {});
      next.source = saved ? 'saved' : 'journey';

      const savedAt = Date.parse(next.savedAt || '');
      if (!Number.isFinite(savedAt) || Date.now() - savedAt > BOOKING_SLOT_MAX_AGE_MS) {
        next.date = '';
        next.slot = '';
      }

      if (context.packageId && context.packageId !== next.cardPackageId) {
        next.cardPackageId = context.packageId;
        next.packageId = context.packageId;
        if (context.service) next.service = context.service;
        if (context.petType && context.petType !== next.petType) {
          next.petType = context.petType;
          // A saved pet of the other type no longer fits the journey.
          if (next.petId && next.petId !== 'new') next.petId = '';
        }
        next.source = 'journey';
      }
      if (!next.petType) next.petType = context.petType;
      if (!next.service) next.service = context.service;

      if (context.storeId && context.storeId !== next.pageStoreId) {
        // A different store page: its clinic replaces the saved one, and the saved
        // doctor, date and slot belonged to the old clinic.
        next.pageStoreId = context.storeId;
        next.city = '';
        next.centreId = '';
        next.doctorId = '';
        next.date = '';
        next.slot = '';
        next.useStore = true;
      }
      return next;
    }

    /** Put the store page's city (and, once clinics are known, its clinic) into the booking. */
    function applyStorePlacement() {
      if (!booking.useStore && booking.city) return;
      const placed = placeStore(readPageContext().store);
      if (placed.city) {
        booking.city = placed.city;
        // A store-list guess made while VetBuddy was loading is replaced, never kept.
        booking.centreId = placed.centreId;
      } else if (placed.unknownCity) {
        setStatus('city', 'info', `We can't take bookings in ${placed.unknownCity} online yet. Please choose another city.`);
      }
      // The clinic is only placed for good once VetBuddy's clinic list is in.
      if (!clinicsUnavailable) delete booking.useStore;
    }

    /** Fill the pet list (saved pets when logged in) and pick the pet the journey points to. */
    function chooseOpeningPet() {
      const petType = choosePetOnOpen(populatePets(booking.petType));
      if (petType) booking.petType = petType;
    }

    async function loadClientsAndCities() {
      // Cities from Shopify straight away; clinics follow from VetBuddy.
      clinicsUnavailable = true;
      cityGroups = buildCityGroups([]);
      applyStorePlacement();
      populateCities();
      setStatus('city', null);
      setStatus('centre', 'loading', 'Loading clinics...');
      clinicSelect.disabled = true;

      const phone = customerPhoneNumber();
      isUsingTemporaryPhone = !phone;
      // Without a mobile number the booking goes in with no VetBuddy IDs, and the booking after
      // payment registers the customer from the checkout phone, so say which number counts.
      if (guestPhoneNote) guestPhoneNote.classList.toggle('hidden', !!phone);
      // A guest has no saved pets to look up, so their pet fields are ready straight away.
      if (!phone) chooseOpeningPet();
      // The city can only be acted on once its clinics are in.
      citySelect.disabled = true;
      clientData = await fetchClientData(phone);
      if (!drawerElement.isConnected) return;

      if (phone) chooseOpeningPet();

      clinicsUnavailable = !clientData;
      setStatus('centre', null);
      cityGroups = buildCityGroups(clientData ? clientData.clinics : []);
      applyStorePlacement();
      if (booking.city && !cityGroups.has(booking.city)) booking.city = '';
      populateCities();
    }

    /**
     * Initialize drawer when opened
     */
    async function initializeDrawer() {
      clearError();
      // Only a logged-in customer's pets come from VetBuddy, so only they wait behind the
      // drawer-wide loader; a guest can fill in their pet while the clinics load.
      if (drawerLoading && customerPhoneNumber()) drawerLoading.style.display = 'flex';
      cartServiceLines = null;
      isSubmitting = false;
      if (submitButtonText) submitButtonText.style.display = '';
      if (submitButtonSpinner) submitButtonSpinner.classList.add('hidden');

      catalog = readCatalog();
      stores = readStores();
      // Pages without the store list still know the customer's own store.
      const selectedStore = readSelectedStore();
      if (selectedStore && selectedStore.city &&
          !stores.some((store) => (store.storeDetailsUrl || store.name) === (selectedStore.storeDetailsUrl || selectedStore.name))) {
        stores.push(selectedStore);
      }
      const seenServices = new Set();
      services = [];
      catalog.forEach((item) => {
        if (seenServices.has(item.service)) return;
        seenServices.add(item.service);
        services.push({ value: item.service, label: item.service });
      });

      booking = buildBooking(readSavedBooking(), readPageContext());

      resetSelect(clinicSelect, 'Choose a clinic *');
      resetSelect(packageSelect, 'Select Package *');
      resetDoctor();
      resetSlots();
      dateInput.disabled = true;
      fillSelect(serviceTypeSelect, 'Select Service *', services, booking.service);

      try {
        // The bag decides which times are still free for this pet and doctor, so it is
        // read before the cascade renders any slots.
        await Promise.all([loadClientsAndCities(), loadCartServiceLines()]);
        if (!drawerElement.isConnected) return;
        updateCartServiceMessage();
      } finally {
        if (drawerLoading) drawerLoading.style.display = 'none';
      }

      refreshFrom(LEVEL.city);
    }

    // Event Listeners
    petSelect.addEventListener('change', handlePetChange);

    if (newPetSpeciesSelect) {
      newPetSpeciesSelect.addEventListener('change', handlePetChange);
    }

    if (newPetNameInput) {
      newPetNameInput.addEventListener('input', function(e) {
        setStatus('pet', null);
        // Prevent leading spaces and limit consecutive spaces to maximum 2 (between text and at end)
        const currentValue = e.target.value;
        // Remove leading spaces
        let trimmedValue = currentValue.replace(/^\s+/, '');
        // Limit consecutive spaces to maximum 2 spaces (anywhere - between text or at end)
        trimmedValue = trimmedValue.replace(/\s{3,}/g, '  ');

        if (currentValue !== trimmedValue) {
          e.target.value = trimmedValue;
        }
        if (isNewPet) {
          booking.petName = trimmedValue.trim();
          saveBooking();
        }
        checkFormCompletion();
      });
      // The typed name may be a pet already booked in the bag; recheck its times once typed.
      newPetNameInput.addEventListener('change', function() {
        if (isNewPet && lastStaffSlots) {
          rerenderTimes();
          checkFormCompletion();
        }
      });
    }

    citySelect.addEventListener('change', function() {
      setStatus('city', null);
      setStatus('centre', null);
      booking.city = this.value;
      // C1: a new city clears the clinic, doctor, date and slot. The customer chose to
      // move, so the old clinic goes quietly rather than with a "not in this city" line.
      booking.centreId = '';
      booking.date = '';
      booking.slot = '';
      refreshFrom(LEVEL.city);
    });

    clinicSelect.addEventListener('change', function() {
      setStatus('centre', null);
      setStatus('service', null);
      booking.centreId = this.value;
      refreshFrom(LEVEL.centre);
    });

    serviceTypeSelect.addEventListener('change', function() {
      setStatus('service', null);
      setStatus('city', null);
      booking.service = this.value;
      form.dataset.serviceType = booking.service;
      // D1/D2: packages and staff belong to the old service, so they go quietly; no
      // grooming value is left behind on a vet booking, or the other way round.
      booking.packageId = '';
      booking.doctorId = '';
      booking.slot = '';
      updateStaffLabel();
      refreshFrom(LEVEL.packages);
    });

    packageSelect.addEventListener('change', function() {
      setStatus('package', null);
      applyPackage(packagesForPet.find((item) => item.variantId === this.value));
      // D5: a new package is a new reason for visit and duration, so the times reload.
      refreshFrom(LEVEL.slots);
    });

    doctorSelect.addEventListener('change', function() {
      setStatus('doctor', null);
      // E6: the list holds only staff free at the chosen time, so date and time stay.
      booking.doctorId = this.value;
      saveBooking();
      checkFormCompletion();
    });

    // Make date wrapper and icon clickable to open date picker
    if (dateWrapper) {
      dateInput.addEventListener('click', function() {
        if (this.showPicker) {
          try {
            this.showPicker();
          } catch (error) {
            this.focus();
          }
        } else {
          // Avoid recursively triggering the same click handler in browsers
          // that do not support showPicker().
          this.focus();
        }
      });
    }

    dateInput.addEventListener('change', function() {
      setStatus('date', null);
      setStatus('slot', null);
      const { min, max } = dateLimits();
      if (this.value && (this.value < min || this.value > max)) {
        setStatus('date', 'info', `Please pick a date between today and ${max.split('-').reverse().join('/')}.`);
        booking.date = '';
      } else {
        booking.date = this.value;
      }
      booking.slot = '';
      refreshFrom(LEVEL.slots);
    });

    timeSlotSelect.addEventListener('change', function() {
      // Not refreshFrom: that would drop the times still arriving from slower staff.
      if (!slotsLoading) setStatus('slot', null);
      booking.slot = this.value;
      stepDoctors();
      saveBooking();
      checkFormCompletion();
    });

    termsCheckbox.addEventListener('change', checkFormCompletion);

    // An incomplete form would hit the browser's own required-field check before 'submit'
    // fires, which says nothing useful for these selects; point to the missing field instead.
    submitButton.addEventListener('click', function(e) {
      if (isSubmitting || checkFormCompletion()) return;
      e.preventDefault();
      clearError();
      showMissingField();
    });

    form.addEventListener('submit', handleFormSubmit);

    // Initialize when drawer opens
    // drawerElement IS the drawer element, so use it directly
    if (drawerElement.classList.contains('available-service-drawer')) {
      // Function to handle drawer opening
      function handleDrawerOpen() {
        if (!drawerElement.dataset.drawerInitialized) {
          drawerElement.dataset.drawerInitialized = 'true';
          initializeDrawer();
        }
      }

      // Function to handle drawer closing
      function handleDrawerClose() {
        drawerElement.dataset.drawerInitialized = '';
        // Drop any cascade still running; the booking object is already saved.
        runSeq++;
        cartServiceLines = null;
        // Reset button state
        if (submitButtonText) submitButtonText.style.display = '';
        if (submitButtonSpinner) submitButtonSpinner.classList.add('hidden');
        if (timeSlotLoader) timeSlotLoader.style.display = 'none';
        // Ensure drawer loading indicator is hidden when drawer closes
        if (drawerLoading) drawerLoading.style.display = 'none';
      }

      // Check if drawer is already open
      if (drawerElement.classList.contains('is-open')) {
        handleDrawerOpen();
      }

      // Listen for custom drawer-opened event (dispatched by openDrawer function)
      drawerElement.addEventListener('drawer-opened', function() {
        setTimeout(() => {
          handleDrawerOpen();
        }, 50);
      });

      // MutationObserver as backup to detect class changes
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.attributeName === 'class') {
            if (drawerElement.classList.contains('is-open')) {
              handleDrawerOpen();
            } else {
              handleDrawerClose();
            }
          }
        });
      });

      observer.observe(drawerElement, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    // Mark only after all listeners and observers have been safely bound.
    drawerElement.dataset.bookingDrawerBound = 'true';
    drawerElement.dataset.initialized = 'true';
    return true;
}


/**
 * Initialize all booking drawers on page load
 */
function initAllBookingDrawers() {
    const drawers = document.querySelectorAll('.available-service-drawer[data-drawer-type="booking"]:not([data-booking-drawer-bound="true"])');
    
    drawers.forEach((drawer) => {
      const productId = drawer.id.replace('drawer-booking-', '');
      const form = drawer.querySelector('.available-service-drawer__form');
      
      if (form && form.dataset.productId) {
        const productData = {
          productId: form.dataset.productId,
          variantId: form.dataset.variantId || '',
          productTitle: form.dataset.productTitle || '',
          serviceType: form.dataset.serviceType || '',
          reasonForVisit: form.dataset.reasonForVisit || ''
        };

        // Get customer phone if available
        if (!drawer.dataset.customerPhone) {
          const uniqueId = drawer.dataset.uniqueId || productId;
          const customerPhoneScript = document.querySelector(`script#customer-phone-data-${uniqueId}`);
          if (!customerPhoneScript) {
            const fallbackScript = document.querySelector('script[data-customer-phone]');
            if (fallbackScript) {
              try {
                const phoneData = JSON.parse(fallbackScript.textContent);
                if (phoneData.phone) {
                  drawer.dataset.customerPhone = phoneData.phone;
                }
              } catch (e) {
                const phone = fallbackScript.getAttribute('data-customer-phone');
                if (phone) {
                  drawer.dataset.customerPhone = phone;
                }
              }
            }
          } else {
            try {
              const phoneData = JSON.parse(customerPhoneScript.textContent);
              if (phoneData.phone) {
                drawer.dataset.customerPhone = phoneData.phone;
              }
            } catch (e) {
              const phone = customerPhoneScript.getAttribute('data-customer-phone');
              if (phone) {
                drawer.dataset.customerPhone = phone;
              }
            }
          }
        }

        initBookingDrawer(drawer, productData);
      }
    });
}

// Expose initializers for lazily rendered/replaced sections and direct Book Now opens.
window.initBookingDrawer = initBookingDrawer;
window.initAllBookingDrawers = initAllBookingDrawers;

// Initialize on DOM ready (no API calls until drawer opens)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllBookingDrawers);
} else {
  initAllBookingDrawers();
}

// Re-initialize when new content is added (for dynamic content)
// Use a single global observer with debouncing to prevent performance issues
if (typeof MutationObserver !== 'undefined') {
  let bookingDrawerObserver = null;
  let isProcessingBookingMutations = false;
  let bookingMutationTimer = null;

function initBookingDrawerObserver() {
  // Prevent multiple observers
  if (bookingDrawerObserver) {
    return;
  }

  // Scope the dynamic drawer observer to the available-service section only.
  // Do NOT fall back to document.body; observing the full page with subtree:true
  // creates excessive callbacks during unrelated page animations/updates.
  const availableServiceSection = document.querySelector('[data-available-service-section]');
  if (!availableServiceSection) {
    console.error('Booking drawer observer not initialized: [data-available-service-section] was not found.');
    return;
  }

  function getBookingProductData(drawer) {
    if (!drawer) return null;

    const form = drawer.querySelector('.available-service-drawer__form');
    if (!form || !form.dataset.productId) {
      return null;
    }

    return {
      productId: form.dataset.productId,
      variantId: form.dataset.variantId || '',
      productTitle: form.dataset.productTitle || '',
      serviceType: form.dataset.serviceType || '',
      reasonForVisit: form.dataset.reasonForVisit || ''
    };
  }

  function queueDrawerForInitialization(drawer, drawerSet) {
    if (!drawer || drawer.dataset.bookingDrawerBound === 'true') {
      return;
    }

    if (drawer.classList &&
        drawer.classList.contains('available-service-drawer') &&
        drawer.dataset.drawerType === 'booking') {
      drawerSet.add(drawer);
    }
  }

  bookingDrawerObserver = new MutationObserver(function(mutations) {
    // Guard against recursive calls
    if (isProcessingBookingMutations) {
      return;
    }

    // Debounce mutations to batch process them
    if (bookingMutationTimer) {
      clearTimeout(bookingMutationTimer);
    }

    bookingMutationTimer = setTimeout(function() {
      isProcessingBookingMutations = true;

      try {
        const drawersToInitialize = new Set();

        mutations.forEach(function(mutation) {
          mutation.addedNodes.forEach(function(node) {
            if (!node || node.nodeType !== 1) {
              return;
            }

            // Case 1: the added node itself is a booking drawer.
            queueDrawerForInitialization(node, drawersToInitialize);

            // Case 2: booking drawers were added inside a wrapper node.
            if (node.querySelectorAll) {
              node.querySelectorAll('.available-service-drawer[data-drawer-type="booking"]:not([data-booking-drawer-bound="true"])')
                .forEach(function(drawer) {
                  queueDrawerForInitialization(drawer, drawersToInitialize);
                });
            }

            // Case 3: child markup was added into an existing drawer that previously
            // failed initialization because required elements were not ready yet.
            if (node.closest) {
              const parentDrawer = node.closest('.available-service-drawer[data-drawer-type="booking"]:not([data-booking-drawer-bound="true"])');
              queueDrawerForInitialization(parentDrawer, drawersToInitialize);
            }
          });
        });

        drawersToInitialize.forEach(function(drawer) {
          const productData = getBookingProductData(drawer);
          if (!productData) {
            return;
          }

          initBookingDrawer(drawer, productData);
        });
      } finally {
        isProcessingBookingMutations = false;
      }
    }, 100); // Debounce by 100ms
  });

  // Observe only the available-service section. Never observe document.body.
  bookingDrawerObserver.observe(availableServiceSection, {
    childList: true,
    subtree: true,
    attributes: false
  });

  // Catch drawers that already exist inside the section before the observer starts.
  initAllBookingDrawers();
}
  // Initialize observer after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBookingDrawerObserver);
  } else {
    initBookingDrawerObserver();
  }

  // available-service.js replaces lazy sections after this script has loaded.
  // Re-scan drawers when that happens so Book Now opens an initialized drawer.
  document.addEventListener('available-service:section-loaded', function() {
    initAllBookingDrawers();
  });
}